/**
 * /cabo-verde — BSGOTECH × Cabo Verde Strategic Initiative 2026.
 *
 * Additive dedicated page inside the existing BSGOTECH website. Inherits the
 * dark navy visual identity, extends it with restrained gold accents and
 * institutional presentation styling. Sixteen sections + sticky in-page nav.
 *
 * IMPORTANT compliance rules baked into the copy and UI:
 *  - No claim of an existing BSGOTECH Cabo Verde office / operation
 *  - No fake statistics, no fake partners, no fake government endorsements
 *  - Maturity/status badges preserved: LIVE · MVP · CONCEPT · FUTURE
 *  - Nu Bai (with its Way pillar) is NOT a bank — that disclaimer is prominent
 *  - Wise Hustlers is only referenced as "development scope prepared with"
 *  - No people photographs — abstract maps / lines / diagrams only
 */
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ChevronRight, ShieldCheck, MapPin, Layers, Cpu,
  Network, Waves, Sun, Zap, Car, Boxes, Landmark, Users2, Building2,
  Handshake, FileText, Mail, ArrowRight, ArrowLeft, ChevronDown, Menu, X,
  Globe, Compass, MessageCircle, Target, BookOpen, Download,
} from "lucide-react";

import { LangCtx, LanguageSwitcher } from "@/i18n/neunexus";

const P = {
  bg:      "#050813",
  bg2:     "#0A0F1F",
  surface: "rgba(255,255,255,0.03)",
  glass:   "rgba(255,255,255,0.045)",
  border:  "rgba(255,255,255,0.08)",
  ink:     "#F2F5F8",
  dim:     "rgba(242,245,248,0.68)",
  silver:  "#B8BFC6",
  gold:    "#C9A24A",
  cyan:    "#22D3EE",
  green:   "#34D399",
  purple:  "#B688FF",
};

const NAV = [
  { id: "overview", label: "Overview" },
  { id: "solutions", label: "Solutions", target: "solutions",
    groupTitle: "BSGOTECH · Business divisions",
    children: [
      { id: "digital", label: "NEXIS",         subtitle: "Technology & Digital — parent of Nu Bai and Eco Collection" },
      { id: "nubai",   label: "Nu Bai",         subtitle: "Digital Mobility & Services (NEXIS product)", key: "nubai-sol" },
      { id: "eco",     label: "Eco Collection", subtitle: "Digital Tourism & Market Access (NEXIS product)", key: "eco-sol" },
      { id: "hx",      label: "HX Ecosystems",  subtitle: "Integrated Infrastructure" },
    ] },
  { id: "projects", label: "Projects", target: "nubai",
    parentItem: { id: "nubai", label: "Nu Bai", subtitle: "Digital Mobility & Services · Cabo Verde ecosystem in development" },
    groupTitle: "Nu Bai · Ecosystem pillars",
    children: [
      { id: "nubai",        label: "Move",   subtitle: "Mobility" },
      { id: "nubai-pay",    label: "Way",    subtitle: "Payment pathway through licensed partners" },
      { id: "nubai-future", label: "Future", subtitle: "Family & financial goal concepts" },
      { id: "nubai-point",  label: "Point",  subtitle: "Physical proximity access", key: "point" },
    ] },
  { id: "approach",  label: "Strategy" },
  { id: "documents", label: "Documents" },
  { id: "contact",   label: "Contact" },
];

// Flat map used for the section-observer that highlights the active top-level item.
const NAV_SECTION_TO_TOPLEVEL = {
  overview: "overview",
  institutional: "overview",
  approach: "approach",
  solutions: "solutions",
  eco: "solutions",
  "eco-market-access": "solutions",
  nubai: "projects",
  pilot: "approach",
  "nubai-pay": "projects",
  "nubai-point": "projects",
  "nubai-future": "projects",
  digital: "solutions",
  hx: "solutions",
  documents: "documents",
  contact: "contact",
};

const scrollTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const track = (event, payload = {}) => {
  try {
    (window.dataLayer = window.dataLayer || []).push({ event, ...payload });
    // eslint-disable-next-line no-console
    console.debug("[cabo-verde]", event, payload);
  } catch { /* noop */ }
};

const PremiumVisual = ({ src, alt, tone = "cyan", testid, className = "" }) => {
  const glow = { cyan: P.cyan, gold: P.gold, green: P.green, purple: P.purple }[tone] || P.cyan;
  return (
    <motion.figure
      initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }}
      className={`relative rounded-2xl overflow-hidden ${className}`}
      style={{
        background: P.bg2,
        border: `1px solid ${P.border}`,
        boxShadow: `0 0 0 1px ${glow}22, 0 30px 80px -30px ${glow}55`,
      }}
      data-testid={testid}
    >
      <img src={src} alt={alt} loading="lazy" decoding="async" width="1376" height="768"
           className="w-full h-auto object-cover block" />
      <div className="pointer-events-none absolute inset-0"
           style={{ background: `linear-gradient(180deg, transparent 55%, rgba(5,8,19,0.55) 100%)` }} />
    </motion.figure>
  );
};

const SectionHeader = ({ eyebrow, title, subtitle, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-4xl"}>
    {eyebrow && (
      <div
        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10.5px] uppercase tracking-[0.32em] font-semibold"
        style={{ background: `${P.gold}14`, border: `1px solid ${P.gold}44`, color: P.gold }}
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.gold }} />
        {eyebrow}
      </div>
    )}
    <h2
      className="mt-4 font-semibold tracking-tight text-[26px] sm:text-[32px] lg:text-[40px] leading-[1.05]"
      style={{ color: "#fff", letterSpacing: "-0.02em" }}
    >
      {title}
    </h2>
    {subtitle && (
      <p className="mt-4 text-[14px] leading-relaxed" style={{ color: P.dim }}>
        {subtitle}
      </p>
    )}
  </div>
);

const StatusBadge = ({ tone, children }) => {
  const tones = {
    live:    { c: P.green,  label: "Live" },
    mvp:     { c: P.cyan,   label: "MVP" },
    concept: { c: P.purple, label: "Concept" },
    future:  { c: P.gold,   label: "Future" },
  };
  const t = tones[tone] || tones.future;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9.5px] uppercase tracking-[0.28em] font-semibold"
      style={{ background: `${t.c}14`, border: `1px solid ${t.c}55`, color: t.c }}
    >
      <span className="w-1 h-1 rounded-full" style={{ background: t.c }} />
      {children || t.label}
    </span>
  );
};

/* --------------------------------------------------------- SVG · islands */
const IslandsMap = () => (
  <svg viewBox="0 0 600 420" className="w-full h-auto max-w-xl" aria-hidden="true">
    <defs>
      <linearGradient id="isl" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%"  stopColor={P.cyan} stopOpacity="0.65" />
        <stop offset="100%" stopColor={P.gold} stopOpacity="0.35" />
      </linearGradient>
      <radialGradient id="glow" cx="50%" cy="50%" r="60%">
        <stop offset="0%"  stopColor={P.cyan} stopOpacity="0.25" />
        <stop offset="100%" stopColor={P.cyan} stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="300" cy="210" r="180" fill="url(#glow)" />
    {/* Barlavento */}
    <ellipse cx="130" cy="145" rx="22" ry="10" fill="url(#isl)" />
    <ellipse cx="185" cy="160" rx="14" ry="8" fill="url(#isl)" />
    <ellipse cx="235" cy="140" rx="17" ry="8" fill="url(#isl)" />
    <ellipse cx="300" cy="150" rx="19" ry="10" fill="url(#isl)" />
    <ellipse cx="360" cy="165" rx="14" ry="7" fill="url(#isl)" />
    <ellipse cx="415" cy="135" rx="16" ry="9" fill="url(#isl)" />
    {/* Sotavento */}
    <ellipse cx="255" cy="260" rx="20" ry="9" fill="url(#isl)" />
    <ellipse cx="320" cy="280" rx="15" ry="7" fill="url(#isl)" />
    <ellipse cx="380" cy="290" rx="22" ry="12" fill="url(#isl)" />
    <ellipse cx="445" cy="270" rx="13" ry="7" fill="url(#isl)" />
    {/* connecting network lines */}
    {[
      "M130,145 L185,160 L235,140 L300,150 L360,165 L415,135",
      "M255,260 L320,280 L380,290 L445,270",
      "M300,150 L320,280", "M235,140 L255,260", "M415,135 L445,270",
    ].map((d, i) => (
      <path key={i} d={d} stroke={P.cyan} strokeWidth="0.6" fill="none" opacity="0.5" />
    ))}
    {/* pulsing hubs */}
    {[[300,150],[380,290]].map(([x,y],i)=>(
      <circle key={i} cx={x} cy={y} r="4" fill={P.gold} />
    ))}
    <text x="300" y="380" textAnchor="middle" fill={P.silver} fontSize="10" letterSpacing="4">
      CABO VERDE · ATLANTIC ARCHIPELAGO
    </text>
  </svg>
);

/* --------------------------------------------------------- data */
const solutions = [
  { id: "nubai",     icon: Car,       tone: "mvp",     color: P.cyan,
    title: "Nu Bai", status: "MVP / PILOT DEVELOPMENT", category: "NEXIS · Digital Mobility & Services",
    desc: "Ecossistema Nu Bai — plataforma digital de mobilidade e serviços com quatro pilares: MOVE, WAY, FUTURE e POINT. Todas as camadas financeiras dependem de bancos, fintechs ou PSPs devidamente licenciados.",
    tags: ["Move", "Way", "Future", "Point"],
    cta: "Explore Nu Bai", href: "#nubai" },
  { id: "eco",       icon: Waves,     tone: "live",    color: P.green,
    title: "Eco Collection", status: "LIVE / VALIDATION", category: "NEXIS · Digital Tourism & Market Access",
    desc: "Plataforma digital que liga alojamento, experiências, transfers, guias e operadores locais a canais de distribuição B2B internacionais. Piloto de acesso ao mercado italiano em avaliação.",
    cta: "Explore Eco Collection", href: "#eco" },
  { id: "hx",        icon: Boxes,     tone: "future",  color: P.gold,
    title: "HX Ecosystems", status: "FUTURE ROADMAP", category: "Integrated Infrastructure",
    desc: "Visão de infraestrutura integrada — solar, água, armazenamento de energia, e-mobilidade e módulos — sujeita a viabilidade local, parceiros técnicos e validação.",
    tags: ["Solar", "Water", "Energy Storage", "E-Mobility", "Modular", "Digital"],
    cta: "See Vision", href: "#hx" },
];

const documents = [
  { id: "integrated-strategy", title: "BSGOTECH × CABO VERDE", subtitle: "Integrated Strategy 2026 · 15-page Institutional Deck",
    badge: "PUBLIC · FEATURED", tone: "live", featured: true,
    desc: "Institutional presentation covering the BSGOTECH bridge, business divisions (NEXIS with Nu Bai and Eco Collection, HX Ecosystems), maturity framework and the proposed pilot hub for Cabo Verde.",
    href: "/downloads/BSGOTECH_Cabo_Verde_Integrated_Strategy.pdf",
    cta: "View Presentation", event: "integrated_strategy_opened",
    secondary: { href: "/downloads/BSGOTECH_Cabo_Verde_Integrated_Strategy.pdf", label: "Download PDF", download: true } },
  { id: "presentation", title: "BSGOTECH × CABO VERDE", subtitle: "Strategic Presentation (Legacy v2)",
    badge: "PUBLIC · EXECUTIVE", tone: "live",
    desc: "Institutional overview, solution portfolio, maturity framework and Cabo Verde market approach.",
    href: "/downloads/apresentacao_bsgotech_caboverde_v2.pdf",
    cta: "View Presentation", event: "strategic_presentation_opened" },
  { id: "executive-brief", title: "BSGOTECH × CABO VERDE", subtitle: "Executive Brief",
    badge: "PUBLIC · EXECUTIVE", tone: "live",
    desc: "Condensed executive brief for institutional dialogue — approach, capabilities and pilot pathway on a single deck.",
    href: "/downloads/bsgotech-cabo-verde-executiva.pdf",
    cta: "View Executive Brief", event: "executive_brief_opened" },
  { id: "nubai-scope", title: "NU BAI", subtitle: "Mobility & Digital Financial Ecosystem · Phase 1",
    badge: "CONTROLLED · TECHNICAL", tone: "mvp",
    desc: "Full Phase 1 platform architecture: Passenger App, Driver App, Admin Dashboard, backend, ride matching, GPS live tracking, notifications and payment integration subject to provider availability.",
    prepared: "Prepared with: Wise Hustlers Technologies Pvt. Ltd.",
    href: "/downloads/NuBai_Mobility_Digital_Financial_Ecosystem_Phase_1.pdf",
    cta: "View Phase 1 Scope", event: "phase1_scope_opened" },
  { id: "nubai-status", title: "NU BAI", subtitle: "MVP Status Report v2",
    badge: "CONTROLLED", tone: "mvp",
    desc: "Latest MVP status snapshot — development progress, pilot readiness signals and identified follow-up items.",
    href: "/downloads/nu-bai-mvp-status-report-v2.pdf",
    cta: "View Status Report", event: "nubai_status_opened" },
  { id: "investment-memo", title: "NU BAI", subtitle: "Investment Memorandum v3",
    badge: "CONFIDENTIAL · UPON AUTHORIZATION", tone: "concept",
    desc: "Investment thesis, unit economics and go-to-market considerations. Confidential — available upon written request and internal authorization.",
    href: "#request-access", request: true,
    cta: "Request Access", event: "investment_memo_requested" },
  { id: "financial-model", title: "NU BAI", subtitle: "Phase 1 Financial Model · 36 months",
    badge: "CONFIDENTIAL · UPON AUTHORIZATION", tone: "concept",
    desc: "Detailed 36-month financial projection for Phase 1: revenue drivers, operating costs, capex profile and sensitivity analysis. Confidential — available upon written request and internal authorization.",
    href: "#request-access", request: true,
    cta: "Request Access", event: "financial_model_requested" },
];

/* --------------------------------------------------------- Page */
export default function CaboVerdePage() {
  const [activeNav, setActiveNav] = React.useState("overview");
  const { lang } = React.useContext(LangCtx);

  // ---- SEO — re-applies on every lang change so it wins over LangProvider
  React.useEffect(() => {
    document.title = "BSGOTECH × Cabo Verde | Strategic Initiative 2026";
    const setMeta = (name, content) => {
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
      m.setAttribute("content", content);
    };
    setMeta("description", "BSGOTECH strategic initiative for digital tourism, mobility and locally validated technology solutions in Cabo Verde.");
  }, [lang]);

  React.useEffect(() => { track("cabo_verde_landing_opened"); }, []);

  // ---- Section-observer for sticky nav highlight
  React.useEffect(() => {
    const opts = { rootMargin: "-40% 0px -55% 0px", threshold: 0 };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const top = NAV_SECTION_TO_TOPLEVEL[e.target.id] || e.target.id;
          setActiveNav(top);
        }
      });
    }, opts);
    Object.keys(NAV_SECTION_TO_TOPLEVEL).forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: P.bg, color: P.ink }} data-testid="cabo-verde-page">
      <LanguageSwitcher />
      {/* Back to BSGOTECH */}
      <div className="absolute top-4 left-4 z-40">
        <Link to="/" data-testid="cv-back"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.24em] font-semibold backdrop-blur-md"
              style={{ background: "rgba(5,8,19,0.6)", border: `1px solid ${P.border}`, color: P.silver }}>
          <ArrowLeft size={11} /> BSGOTECH
        </Link>
      </div>

      {/* Kriolu welcome banner — only when lang === "cv" */}
      {lang === "cv" && (
        <div className="relative border-b" style={{ background: `linear-gradient(90deg, ${P.gold}15 0%, ${P.bg} 60%)`, borderColor: P.border }} data-testid="cv-kriolu-banner">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ background: `${P.gold}18`, border: `1px solid ${P.gold}55`, color: P.gold }}>
              KV · Kriolu di Kabuverdi
            </div>
            <div className="text-[12.5px] leading-relaxed flex-1" style={{ color: P.ink }}>
              <strong style={{ color: "#fff" }}>Bem-bindu.</strong>{" "}
              <span style={{ color: P.dim }}>Nu ta konstrui es prujetu djuntu ku Kabu Verdi. Nu ta kumesa piku, ku parseiru lokal, ku validason kontroladu. Restu di es página sta na Purtuges — lingua ofisial di Kabu Verdi.</span>
            </div>
          </div>
        </div>
      )}

      {/* Sticky in-page nav */}
      <StickyNav activeNav={activeNav} />

      {/* ============ SECTION 01 · HERO ============ */}
      <section id="overview" className="relative pt-24 pb-24 px-6 lg:px-12 overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full" style={{ background: `radial-gradient(circle, ${P.cyan}18 0%, transparent 70%)` }} />
          <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full" style={{ background: `radial-gradient(circle, ${P.gold}12 0%, transparent 70%)` }} />
        </div>
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10.5px] uppercase tracking-[0.32em] font-semibold"
                 style={{ background: `${P.gold}14`, border: `1px solid ${P.gold}44`, color: P.gold }}>
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: P.gold }} />
              Strategic Market Initiative · 2026
            </div>
            <h1 className="mt-6 font-semibold tracking-tight text-[38px] sm:text-[54px] lg:text-[68px] leading-[1.02]"
                style={{ color: "#fff", letterSpacing: "-0.03em" }}>
              BSGOTECH<br /><span style={{ color: P.gold }}>× Cabo Verde</span>
            </h1>
            <p className="mt-5 text-[14px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>
              Digital Solutions · Local Validation · Strategic Partnerships
            </p>
            <div className="mt-8 text-[22px] sm:text-[26px] font-light leading-relaxed" style={{ color: "#fff" }}>
              Tecnologia aplicada.<br />Pilotos locais.<br /><span style={{ color: P.gold }}>Impacto mensurável.</span>
            </div>
            <p className="mt-6 max-w-2xl text-[14px] leading-relaxed" style={{ color: P.dim }}>
              A BSGOTECH está a explorar oportunidades de colaboração em Cabo Verde através de soluções digitais e integradas, com uma abordagem baseada em parceiros locais, validação controlada e resultados mensuráveis.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#solutions" onClick={scrollTo("solutions")} data-testid="cv-cta-solutions"
                 className="inline-flex items-center gap-2 px-6 py-3 text-[12.5px] font-semibold rounded-md"
                 style={{ background: P.gold, color: "#0d0a02" }}>
                Explore Solutions <ArrowUpRight size={13} />
              </a>
              <a href="#institutional" onClick={scrollTo("institutional")} data-testid="cv-cta-institutional"
                 className="inline-flex items-center gap-2 px-6 py-3 text-[12.5px] font-semibold rounded-md"
                 style={{ background: `${P.cyan}18`, color: P.cyan, border: `1px solid ${P.cyan}55` }}>
                Institutional Presentation <FileText size={13} />
              </a>
              <a href="#documents" onClick={scrollTo("documents")} data-testid="cv-cta-docs"
                 className="inline-flex items-center gap-2 px-6 py-3 text-[12.5px] font-semibold rounded-md"
                 style={{ background: P.glass, color: P.ink, border: `1px solid ${P.border}` }}>
                View Documents <ChevronRight size={13} />
              </a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
                      className="relative flex flex-col items-center justify-center">
            <PremiumVisual src="/cabo-verde/visuals/01-hero-archipelago.webp"
              alt="Cinematic aerial view of the Cabo Verde archipelago with subtle digital nodes on selected islands."
              tone="gold" testid="cv-visual-hero" className="w-full max-w-2xl" />
            <div className="mt-4 rounded-lg p-4 max-w-sm w-full text-center"
                 style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.gold }}>Cabo Verde</div>
              <div className="mt-1 text-[12px]" style={{ color: P.dim }}>Market Validation & Partnership Development</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ SECTION 01a · DIVISIONS OVERVIEW (mental model) ============ */}
      <section id="divisions" className="relative py-20 px-6 lg:px-12 border-t overflow-hidden" style={{ borderColor: P.border, background: P.bg2 }} data-testid="cv-divisions-overview">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 w-[500px] h-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: `radial-gradient(circle, ${P.gold}10 0%, transparent 70%)` }} />
        </div>
        <div className="relative max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ background: `${P.gold}14`, border: `1px solid ${P.gold}44`, color: P.gold }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.gold }} />
              What BSGOTECH Can Do
            </div>
            <h2 className="mt-4 font-semibold tracking-tight text-[26px] sm:text-[32px] lg:text-[38px] leading-[1.1]"
                style={{ color: "#fff", letterSpacing: "-0.02em" }}>
              One company. <span style={{ color: P.gold }}>Two business divisions.</span>
            </h2>
            <p className="mt-4 text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
              A BSGOTECH organiza-se em duas divisões permanentes: <strong style={{ color: P.cyan }}>NEXIS</strong> (Technology &amp; Digital, que abrange o ecossistema Nu Bai e a Eco Collection) e <strong style={{ color: P.gold }}>HX Ecosystems</strong> (Integrated Infrastructure).
            </p>
          </div>

          {/* Tree visual */}
          <div className="mt-12 relative" data-testid="cv-divisions-tree">
            {/* Root card */}
            <div className="flex justify-center">
              <div className="rounded-xl px-6 py-4 text-center"
                   style={{ background: `${P.gold}14`, border: `1px solid ${P.gold}66`, boxShadow: `0 0 0 1px ${P.gold}22, 0 24px 60px -30px ${P.gold}55` }}
                   data-testid="cv-divisions-root">
                <div className="text-[9.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>Parent Company</div>
                <div className="mt-1 text-[22px] font-semibold" style={{ color: "#fff", letterSpacing: "-0.02em" }}>BSGOTECH</div>
                <div className="mt-1 text-[11px]" style={{ color: P.gold }}>The Bridge · Technology · Local Validation</div>
              </div>
            </div>

            {/* Connector SVG (desktop only) — two branches */}
            <svg viewBox="0 0 800 80" preserveAspectRatio="none" aria-hidden="true"
                 className="hidden md:block w-full h-16 mt-2">
              <path d="M400,0 L400,30 L200,30 L200,80" stroke={P.gold} strokeWidth="1" fill="none" opacity="0.55" />
              <path d="M400,30 L600,30 L600,80" stroke={P.gold} strokeWidth="1" fill="none" opacity="0.55" />
              <circle cx="400" cy="30" r="3" fill={P.gold} />
              <circle cx="200" cy="80" r="3" fill={P.gold} />
              <circle cx="600" cy="80" r="3" fill={P.gold} />
            </svg>

            {/* Two division cards */}
            <div className="mt-2 md:mt-0 grid md:grid-cols-2 gap-4">
              {/* NEXIS card — with sub-products */}
              <div className="rounded-xl p-5 flex flex-col"
                   style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.cyan}88` }}
                   data-testid="cv-divisions-digital">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center"
                       style={{ background: `${P.cyan}14`, border: `1px solid ${P.cyan}55` }}>
                    <Cpu size={18} style={{ color: P.cyan }} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.cyan }}>NEXIS</div>
                </div>
                <div className="mt-3 text-[15px] font-semibold" style={{ color: "#fff" }}>Technology &amp; Digital</div>
                <p className="mt-1.5 text-[12px] leading-relaxed" style={{ color: P.dim }}>
                  Divisão digital da BSGOTECH — plataformas, dados, integrações. Reúne os dois produtos NEXIS ativos em Cabo Verde:
                </p>

                <div className="mt-4 grid gap-2">
                  <a href="#nubai" onClick={scrollTo("nubai")}
                     data-testid="cv-divisions-nexis-nubai"
                     className="rounded-md p-3 transition"
                     style={{ background: `${P.cyan}0a`, border: `1px solid ${P.cyan}33` }}
                     onMouseEnter={(e) => { e.currentTarget.style.background = `${P.cyan}14`; }}
                     onMouseLeave={(e) => { e.currentTarget.style.background = `${P.cyan}0a`; }}>
                    <div className="flex items-center gap-2">
                      <Car size={13} style={{ color: P.cyan }} />
                      <span className="text-[13px] font-semibold" style={{ color: "#fff" }}>Nu Bai</span>
                      <span className="text-[9.5px] uppercase tracking-[0.24em] font-semibold ml-auto" style={{ color: P.cyan }}>Move · Way · Future · Point</span>
                    </div>
                    <div className="mt-1 text-[11.5px]" style={{ color: P.dim }}>Digital Mobility &amp; Services</div>
                  </a>
                  <a href="#eco" onClick={scrollTo("eco")}
                     data-testid="cv-divisions-nexis-eco"
                     className="rounded-md p-3 transition"
                     style={{ background: `${P.green}0a`, border: `1px solid ${P.green}33` }}
                     onMouseEnter={(e) => { e.currentTarget.style.background = `${P.green}14`; }}
                     onMouseLeave={(e) => { e.currentTarget.style.background = `${P.green}0a`; }}>
                    <div className="flex items-center gap-2">
                      <Waves size={13} style={{ color: P.green }} />
                      <span className="text-[13px] font-semibold" style={{ color: "#fff" }}>Eco Collection</span>
                      <StatusBadge tone="live">LIVE / VALIDATION</StatusBadge>
                    </div>
                    <div className="mt-1 text-[11.5px]" style={{ color: P.dim }}>Digital Tourism &amp; Market Access</div>
                  </a>
                </div>
              </div>

              {/* HX Ecosystems card */}
              <a href="#hx" onClick={scrollTo("hx")}
                 data-testid="cv-divisions-hx"
                 className="group rounded-xl p-5 flex flex-col transition"
                 style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}88` }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center"
                       style={{ background: `${P.gold}14`, border: `1px solid ${P.gold}55` }}>
                    <Building2 size={18} style={{ color: P.gold }} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.gold }}>HX ECOSYSTEMS</div>
                </div>
                <div className="mt-3 text-[15px] font-semibold" style={{ color: "#fff" }}>Integrated Infrastructure</div>
                <p className="mt-1.5 text-[12px] leading-relaxed" style={{ color: P.dim }}>
                  Divisão de infraestrutura integrada — camada física, energia e mobilidade, sujeita a viabilidade local e parceiros técnicos.
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {["Solar", "Water", "Energy Storage", "E-Mobility", "Modular"].map((it) => (
                    <span key={it} className="text-[10px] px-2 py-0.5 rounded-full"
                          style={{ background: `${P.gold}0f`, border: `1px solid ${P.gold}33`, color: P.gold }}>{it}</span>
                  ))}
                </div>
                <div className="mt-auto pt-4 inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.24em]" style={{ color: P.gold }}>
                  Explore <ChevronRight size={12} />
                </div>
              </a>
            </div>
          </div>

          {/* Nu Bai ecosystem note */}
          <div className="mt-10 rounded-xl px-6 py-5 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6"
               style={{ background: `${P.cyan}0a`, border: `1px solid ${P.cyan}33` }}
               data-testid="cv-divisions-projects-note">
            <div className="shrink-0 w-10 h-10 rounded-md flex items-center justify-center"
                 style={{ background: `${P.cyan}14`, border: `1px solid ${P.cyan}55` }}>
              <Network size={18} style={{ color: P.cyan }} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.cyan }}>Nu Bai · Um único ecossistema</div>
              <div className="mt-1 text-[13.5px] leading-relaxed" style={{ color: P.ink }}>
                <strong style={{ color: "#fff" }}>Nu Bai</strong> é um <em>ecossistema único</em> dentro da <span style={{ color: P.cyan }}>NEXIS</span>, com quatro pilares: <strong>MOVE</strong> (mobilidade), <strong>WAY</strong> (pagamentos através de parceiros licenciados), <strong>FUTURE</strong> (poupança orientada a objectivos familiares) e <strong>POINT</strong> (infraestrutura de proximidade). Não são projectos independentes.
              </div>
            </div>
            <a href="#nubai" onClick={scrollTo("nubai")}
               data-testid="cv-divisions-see-nubai"
               className="inline-flex items-center gap-2 px-4 py-2 text-[11.5px] font-semibold rounded-md whitespace-nowrap"
               style={{ background: `${P.cyan}18`, color: P.cyan, border: `1px solid ${P.cyan}55` }}>
              See Nu Bai <ChevronRight size={12} />
            </a>
          </div>
        </div>
      </section>

      {/* ============ SECTION 01b · INSTITUTIONAL PRESENTATION ============ */}
      <section id="institutional" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border, background: `linear-gradient(180deg, ${P.bg} 0%, ${P.bg2} 100%)` }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-3 flex-wrap justify-between">
            <SectionHeader eyebrow="Featured Presentation · Integrated Strategy 2026"
              title="BSGOTECH × Cabo Verde — Integrated Strategy"
              subtitle="Deck institucional de 15 páginas: a ponte BSGOTECH, as duas divisões (NEXIS com Nu Bai e Eco Collection · HX Ecosystems), quadro de maturidade e o Pilot Hub proposto para Cabo Verde." />
            <span className="text-[9.5px] uppercase tracking-[0.28em] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
                  style={{ background: `${P.gold}14`, border: `1px solid ${P.gold}55`, color: P.gold }}>
              PUBLIC · FEATURED
            </span>
          </div>

          <div className="mt-12 grid lg:grid-cols-[1.15fr_1fr] gap-8 items-start">
            {/* Cover — large */}
            <motion.figure
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }}
              className="relative rounded-2xl overflow-hidden"
              style={{
                background: P.bg2,
                border: `1px solid ${P.border}`,
                boxShadow: `0 0 0 1px ${P.gold}22, 0 30px 80px -30px ${P.gold}55`,
              }}
              data-testid="cv-featured-cover"
            >
              <img src="/cabo-verde/presentation/cover.webp"
                   alt="BSGOTECH × Cabo Verde Integrated Strategy 2026 — front cover of the 15-page institutional deck."
                   loading="lazy" decoding="async"
                   className="w-full h-auto object-cover block" />
              <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-full text-[9.5px] uppercase tracking-[0.28em] font-semibold"
                   style={{ background: "rgba(5,8,19,0.72)", border: `1px solid ${P.gold}55`, color: P.gold }}>
                <FileText size={11} /> 15 pages · Institutional Deck
              </div>
              <div className="pointer-events-none absolute inset-0"
                   style={{ background: `linear-gradient(180deg, transparent 55%, rgba(5,8,19,0.55) 100%)` }} />
            </motion.figure>

            {/* Right column — key spreads + CTAs */}
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-2 gap-3">
                {/* Bridge */}
                <motion.figure
                  initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: 0.05 }}
                  className="relative rounded-xl overflow-hidden"
                  style={{ background: P.bg2, border: `1px solid ${P.border}`, boxShadow: `0 0 0 1px ${P.cyan}22` }}
                  data-testid="cv-featured-bridge"
                >
                  <img src="/cabo-verde/presentation/bridge.webp"
                       alt="Spread — The BSGOTECH bridge connecting technology, market and Cabo Verde."
                       loading="lazy" decoding="async"
                       className="w-full h-auto object-cover block" />
                  <div className="absolute bottom-0 left-0 right-0 px-3 py-2 text-[9.5px] uppercase tracking-[0.28em] font-semibold"
                       style={{ background: "linear-gradient(180deg, transparent, rgba(5,8,19,0.85))", color: P.cyan }}>
                    p.03 · The Bridge
                  </div>
                </motion.figure>

                {/* Divisions */}
                <motion.figure
                  initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: 0.10 }}
                  className="relative rounded-xl overflow-hidden"
                  style={{ background: P.bg2, border: `1px solid ${P.border}`, boxShadow: `0 0 0 1px ${P.green}22` }}
                  data-testid="cv-featured-divisions"
                >
                  <img src="/cabo-verde/presentation/divisions.webp"
                       alt="Spread — Four divisions: Eco Collection, Nu Bai, Nu Bai Pay and HX Ecosystems with maturity framework."
                       loading="lazy" decoding="async"
                       className="w-full h-auto object-cover block" />
                  <div className="absolute bottom-0 left-0 right-0 px-3 py-2 text-[9.5px] uppercase tracking-[0.28em] font-semibold"
                       style={{ background: "linear-gradient(180deg, transparent, rgba(5,8,19,0.85))", color: P.green }}>
                    p.05 · Four Divisions
                  </div>
                </motion.figure>
              </div>

              {/* Hub — with mandatory CONCEPT disclaimer overlay */}
              <motion.figure
                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: 0.15 }}
                className="relative rounded-xl overflow-hidden"
                style={{ background: P.bg2, border: `1px solid ${P.gold}44`, boxShadow: `0 0 0 1px ${P.gold}22` }}
                data-testid="cv-featured-hub"
              >
                <img src="/cabo-verde/presentation/hub.webp"
                     alt="Spread — Proposed BSGOTECH Pilot Hub for Cabo Verde. Concept visualization only."
                     loading="lazy" decoding="async"
                     className="w-full h-auto object-cover block" />
                <div className="pointer-events-none absolute inset-0"
                     style={{ background: `linear-gradient(180deg, rgba(5,8,19,0.15) 0%, rgba(5,8,19,0.85) 100%)` }} />
                <div className="absolute top-2.5 left-2.5 flex items-center gap-2 px-2.5 py-1 rounded-full text-[9.5px] uppercase tracking-[0.28em] font-bold"
                     style={{ background: "rgba(201,162,74,0.18)", border: `1px solid ${P.gold}88`, color: P.gold }}
                     data-testid="cv-hub-concept-badge">
                  <ShieldCheck size={11} /> Concept Visualization
                </div>
                <div className="absolute bottom-0 left-0 right-0 px-3 py-2.5 text-[10px] uppercase tracking-[0.28em] font-semibold"
                     style={{ color: "#fff" }}
                     data-testid="cv-hub-concept-caption">
                  p.12 · Proposed Pilot Hub · Not Current Infrastructure
                </div>
              </motion.figure>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3 pt-1">
                <a href="/downloads/BSGOTECH_Cabo_Verde_Integrated_Strategy.pdf"
                   target="_blank" rel="noopener noreferrer"
                   onClick={() => track("integrated_strategy_opened", { source: "featured_section" })}
                   data-testid="cv-featured-view"
                   className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
                   style={{ background: P.gold, color: "#0d0a02" }}>
                  View Presentation <ArrowUpRight size={13} />
                </a>
                <a href="/downloads/BSGOTECH_Cabo_Verde_Integrated_Strategy.pdf"
                   download
                   onClick={() => track("integrated_strategy_downloaded", { source: "featured_section" })}
                   data-testid="cv-featured-download"
                   className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
                   style={{ background: `${P.cyan}18`, color: P.cyan, border: `1px solid ${P.cyan}55` }}>
                  Download PDF <FileText size={13} />
                </a>
                <a href="#documents" onClick={scrollTo("documents")}
                   data-testid="cv-featured-library"
                   className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
                   style={{ background: P.glass, color: P.ink, border: `1px solid ${P.border}` }}>
                  Full Document Library <ChevronRight size={13} />
                </a>
              </div>

              <p className="text-[11.5px] italic leading-relaxed p-4 rounded-md"
                 style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}`, color: P.dim }}
                 data-testid="cv-featured-disclaimer">
                Documento institucional para diálogo estratégico. Todas as imagens e infográficos são visualizações conceituais — não representam infraestrutura, frota, equipa ou parcerias já operacionais em Cabo Verde. Estado real por divisão: NEXIS · Nu Bai (MVP / Pilot Development, com pilares Move · Way · Future · Point) · Eco Collection (LIVE / Validation) · HX Ecosystems (Future Roadmap).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 02 · APPROACH ============ */}
      <section id="approach" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border, background: P.bg2 }}>
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="Our Approach"
            title="Começamos pelo problema, não pela tecnologia."
            subtitle="A BSGOTECH procura compreender primeiro o contexto local, identificar os stakeholders adequados e validar soluções através de pilotos antes de considerar implementação em maior escala." />
          <div className="mt-12 overflow-x-auto">
            <ol className="flex items-stretch gap-3 min-w-[820px]">
              {["Local Partner","Real Problem","Solution","Controlled Pilot","Data & Validation","Scale What Works"].map((step, i) => (
                <React.Fragment key={step}>
                  <li className="flex-1 rounded-lg p-4" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}55` }}>
                    <div className="text-[9.5px] uppercase tracking-[0.28em] font-mono font-semibold" style={{ color: P.gold }}>{String(i+1).padStart(2,"0")}</div>
                    <div className="mt-2 text-[13px] font-semibold" style={{ color: "#fff" }}>{step}</div>
                  </li>
                  {i < 5 && (<div className="self-center shrink-0" style={{ color: P.gold }}><ArrowRight size={16} /></div>)}
                </React.Fragment>
              ))}
            </ol>
          </div>
          <div className="mt-10 max-w-4xl mx-auto">
            <PremiumVisual src="/cabo-verde/visuals/02-validation-model.webp"
              alt="Strategic validation-model diagram — six geometric nodes connected by digital lines over a subtle Cabo Verde contour."
              tone="cyan" testid="cv-visual-validation" />
          </div>
        </div>
      </section>

      {/* ============ SECTION 03 · SOLUTIONS ============ */}
      <section id="solutions" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border }}>
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="Solution Portfolio" title="Solutions for Validation"
            subtitle="Quatro capacidades com níveis de maturidade distintos — cada uma apresentada com o seu estado real de desenvolvimento." />
          <div className="mt-10">
            <PremiumVisual src="/cabo-verde/visuals/03-solution-ecosystem.webp"
              alt="BSGOTECH central digital core connected to four solution modules — Digital Tourism, Digital Mobility, Payment Integration, Future Integrated Infrastructure."
              tone="cyan" testid="cv-visual-ecosystem" />
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-4">
            {solutions.map((s) => (
              <div key={s.id} data-testid={`cv-solution-${s.id}`}
                   className="rounded-xl p-6 flex flex-col"
                   style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `3px solid ${s.color}66` }}>
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center"
                       style={{ background: `${s.color}14`, border: `1px solid ${s.color}55` }}>
                    <s.icon size={18} style={{ color: s.color }} />
                  </div>
                  <StatusBadge tone={s.tone}>{s.status}</StatusBadge>
                </div>
                <div className="mt-4 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>{s.category}</div>
                <h3 className="mt-1 text-[22px] font-semibold tracking-tight" style={{ color: "#fff" }}>{s.title}</h3>
                <p className="mt-3 text-[13px] leading-relaxed" style={{ color: P.dim }}>{s.desc}</p>
                {s.tags && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {s.tags.map((t) => (
                      <span key={t} className="text-[10px] px-2 py-1 rounded-full" style={{ background: `${s.color}12`, color: s.color, border: `1px solid ${s.color}33` }}>{t}</span>
                    ))}
                  </div>
                )}
                {s.disclaimer && (
                  <div className="mt-3 text-[11px] font-semibold tracking-wide uppercase" style={{ color: s.color }}>{s.disclaimer}</div>
                )}
                <a href={s.href} onClick={scrollTo(s.href.replace("#",""))}
                   className="mt-5 inline-flex items-center gap-1.5 self-start text-[11.5px] font-semibold uppercase tracking-[0.2em]"
                   style={{ color: s.color }}>
                  {s.cta} <ChevronRight size={13} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECTION 04 · ECO COLLECTION ============ */}
      <section id="eco" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border, background: `linear-gradient(180deg, ${P.bg} 0%, ${P.bg2} 100%)` }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-3 flex-wrap justify-between">
            <SectionHeader eyebrow="NEXIS · Eco Collection · Digital Tourism & Market Access"
              title="From Local Supply to International Market Access"
              subtitle="Plataforma digital para conectar alojamentos, experiências, transfers e operadores locais com viajantes e canais de distribuição." />
            <StatusBadge tone="live">LIVE / VALIDATION</StatusBadge>
          </div>
          <div className="mt-10">
            <PremiumVisual src="/cabo-verde/visuals/04-eco-collection.webp"
              alt="Eco Collection digital tourism ecosystem — abstract mobile and desktop UI over a stylized coastal Cabo Verde environment."
              tone="green" testid="cv-visual-eco" />
          </div>
          <div className="mt-12 grid lg:grid-cols-3 gap-5">
            {[
              { icon: Landmark, title: "Local Tourism Supply",
                items: ["Accommodation", "Experiences", "Transfers", "Local Operators"] },
              { icon: Network,  title: "Eco Collection · Digital Platform",
                items: ["Destinations", "Full Packages", "Availability Requests", "Vouchers", "Partner Management", "B2B Distribution"], center: true },
              { icon: Users2,   title: "Market Access",
                items: ["Travellers", "Tour Operators", "B2B Partners", "International Markets"] },
            ].map((b, i) => (
              <div key={i} className="rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${b.center ? P.green : `${P.green}33`}` }}>
                <div className="w-10 h-10 rounded-md flex items-center justify-center"
                     style={{ background: `${P.green}14`, border: `1px solid ${P.green}55` }}>
                  <b.icon size={18} style={{ color: P.green }} />
                </div>
                <div className="mt-3 text-[13px] font-semibold" style={{ color: "#fff" }}>{b.title}</div>
                <ul className="mt-3 space-y-1.5 text-[12.5px]" style={{ color: P.dim }}>
                  {b.items.map((x) => (<li key={x} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: P.green }} /><span>{x}</span></li>))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="https://ecocollection.bsgotech.com" target="_blank" rel="noopener noreferrer"
               onClick={() => track("eco_collection_opened")}
               data-testid="cv-eco-open"
               className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
               style={{ background: P.green, color: "#052112" }}>
              Open Eco Collection <ArrowUpRight size={13} />
            </a>
            <a href="#documents" onClick={scrollTo("documents")}
               className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
               style={{ background: P.glass, color: P.ink, border: `1px solid ${P.border}` }}>
              View Platform Overview <ChevronRight size={13} />
            </a>
          </div>
        </div>
      </section>

      {/* ============ SECTION 04b · ECO COLLECTION · MARKET ACCESS ============ */}
      <section id="eco-market-access" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border, background: P.bg2 }} data-testid="cv-market-access">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-3 flex-wrap justify-between">
            <SectionHeader eyebrow="NEXIS · Eco Collection · International Market Access"
              title="Ligar a Oferta Local aos Mercados Internacionais"
              subtitle="Ligamos a oferta turística local a canais de distribuição internacionais estruturados." />
            <StatusBadge tone="live">LIVE / VALIDATION</StatusBadge>
          </div>

          {/* Ecosystem flow diagram — 5-node horizontal */}
          <div className="mt-12 rounded-2xl p-6 lg:p-8" style={{ background: P.surface, border: `1px solid ${P.border}` }} data-testid="cv-ma-flow">
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-center" style={{ color: P.silver }}>
              Fluxo do Ecossistema · Da Oferta Local aos Viajantes Internacionais
            </div>
            <div className="mt-6 grid lg:grid-cols-[1fr_auto_1.1fr_auto_1fr_auto_1fr_auto_1fr] items-stretch gap-3 lg:gap-2">
              {[
                { id: "supply",     code: "01", c: P.green,  icon: Landmark, title: "Oferta Turística Local",
                  items: ["Alojamento", "Experiências", "Transfers", "Guias", "Operadores locais"] },
                null,
                { id: "eco",        code: "02", c: P.cyan,   icon: Network, title: "Eco Collection", center: true,
                  subtitle: "Plataforma Digital & Rede de Destinos",
                  items: ["Gestão de destinos", "Produtos turísticos", "Pedidos de disponibilidade", "Gestão de parceiros", "Distribuição B2B", "Acesso a mercados"] },
                null,
                { id: "market",     code: "03", c: P.gold,   icon: Globe, title: "Acesso a Mercados Internacionais",
                  items: ["Posicionamento", "B2B Pitch Kit", "Prospeção qualificada", "Reuniões B2B"] },
                null,
                { id: "operators",  code: "04", c: P.purple, icon: Handshake, title: "Tour Operators & Parceiros B2B",
                  items: ["Tour Operators qualificados", "Agências", "OTAs / Distribuição"] },
                null,
                { id: "travellers", code: "05", c: P.silver, icon: Users2, title: "Viajantes Internacionais",
                  items: ["Experiência no destino", "Visitas recorrentes", "Passa-palavra"] },
              ].map((n, i) => {
                if (n === null) {
                  return (
                    <div key={`sep-${i}`} className="hidden lg:flex items-center justify-center" aria-hidden="true">
                      <ArrowRight size={16} style={{ color: P.gold, opacity: 0.6 }} />
                    </div>
                  );
                }
                return (
                  <div key={n.id}
                       className="rounded-xl p-4 flex flex-col"
                       style={{
                         background: n.center ? `${n.c}10` : P.bg2,
                         border: `1px solid ${n.center ? `${n.c}66` : P.border}`,
                         borderTop: `2px solid ${n.c}${n.center ? "" : "88"}`,
                         boxShadow: n.center ? `0 0 0 1px ${n.c}22, 0 24px 60px -30px ${n.c}55` : "none",
                       }}
                       data-testid={`cv-ma-node-${n.id}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md flex items-center justify-center"
                           style={{ background: `${n.c}14`, border: `1px solid ${n.c}55` }}>
                        <n.icon size={14} style={{ color: n.c }} />
                      </div>
                      <div className="text-[9.5px] uppercase tracking-[0.28em] font-mono font-semibold" style={{ color: n.c }}>{n.code}</div>
                    </div>
                    <div className="mt-3 text-[13.5px] font-semibold leading-snug" style={{ color: "#fff" }}>{n.title}</div>
                    {n.subtitle && (<div className="mt-1 text-[11px]" style={{ color: n.c }}>{n.subtitle}</div>)}
                    <ul className="mt-3 space-y-1 text-[11.5px] flex-1" style={{ color: P.dim }}>
                      {n.items.map((it) => (
                        <li key={it} className="flex gap-1.5"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: n.c }} />{it}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Italy Market Access Pilot */}
          <div className="mt-12 rounded-2xl p-6 lg:p-8" style={{ background: P.surface, border: `1px solid ${P.gold}44`, borderTop: `2px solid ${P.gold}` }} data-testid="cv-italy-pilot">
            <div className="flex items-start gap-3 flex-wrap justify-between">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ background: `${P.gold}14`, border: `1px solid ${P.gold}44`, color: P.gold }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.gold }} />
                  Acesso ao Mercado Italiano · Piloto de Validação de 90 Dias (Proposto)
                </div>
                <h3 className="mt-4 font-semibold tracking-tight text-[24px] sm:text-[28px]" style={{ color: "#fff", letterSpacing: "-0.02em" }}>
                  Cabo Verde → <span style={{ color: P.gold }}>Itália</span>
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11.5px]">
                  <span className="uppercase tracking-[0.24em] font-semibold" style={{ color: P.silver }}>Mercado:</span>
                  <span style={{ color: P.ink }}>Itália</span>
                  <span style={{ color: P.border }}>·</span>
                  <span className="uppercase tracking-[0.24em] font-semibold" style={{ color: P.silver }}>Duração:</span>
                  <span style={{ color: P.ink }}>90 dias (proposto)</span>
                </div>
              </div>
              <StatusBadge tone="concept">PROPOSAL RECEIVED · UNDER EVALUATION</StatusBadge>
            </div>

            <p className="mt-6 text-[13.5px] leading-relaxed max-w-4xl" style={{ color: P.dim }}>
              <strong style={{ color: "#fff" }}>Objectivo:</strong> Validar se produtos turísticos selecionados de Cabo Verde e parceiros locais podem ser ligados a Tour Operators italianos qualificados e a canais de distribuição B2B.
            </p>

            {/* 6-step process */}
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cv-italy-process">
              {[
                { n: "01", icon: MapPin,         title: "Mapeamento de Mercado", d: "Identificar Tour Operators e agências italianas qualificadas." },
                { n: "02", icon: Compass,        title: "Posicionamento B2B",    d: "Adaptar a proposta de valor da Eco Collection e o B2B Pitch Kit." },
                { n: "03", icon: Users2,         title: "Prospeção Qualificada", d: "Contacto direcionado a 15–20 Tour Operators italianos selecionados (âmbito proposto)." },
                { n: "04", icon: MessageCircle,  title: "Reuniões B2B",          d: "Facilitar reuniões entre operadores interessados e a BSGOTECH / Eco Collection." },
                { n: "05", icon: Target,         title: "Validação Comercial",   d: "Medir interesse, pedidos, objeções e potenciais oportunidades comerciais." },
                { n: "06", icon: ShieldCheck,    title: "Decisão",               d: "GO · ADAPT · STOP." },
              ].map((s) => (
                <div key={s.n} className="rounded-xl p-4" style={{ background: P.bg2, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}66` }} data-testid={`cv-italy-step-${s.n}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: `${P.gold}14`, border: `1px solid ${P.gold}55` }}>
                      <s.icon size={14} style={{ color: P.gold }} />
                    </div>
                    <div className="text-[9.5px] uppercase tracking-[0.28em] font-mono font-semibold" style={{ color: P.gold }}>Etapa {s.n}</div>
                  </div>
                  <div className="mt-3 text-[13.5px] font-semibold" style={{ color: "#fff" }}>{s.title}</div>
                  <div className="mt-1.5 text-[12px] leading-relaxed" style={{ color: P.dim }}>{s.d}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-md p-3 text-[11.5px] italic" style={{ background: `${P.gold}0d`, border: `1px solid ${P.gold}33`, color: P.dim }}>
              15–20 Tour Operators italianos é o <strong style={{ color: P.gold }}>âmbito de prospeção proposto</strong>, não um resultado alcançado. Trata-se de uma metodologia de validação, não uma alegação de entrada no mercado já concretizada.
            </div>
          </div>

          {/* Proposed Collaborator + Future Synergy — side by side */}
          <div className="mt-8 grid lg:grid-cols-2 gap-4">
            {/* SUQ Communication */}
            <div className="rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.cyan}66` }} data-testid="cv-suq-card">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.cyan }}>
                  Apoio Proposto ao Acesso a Mercados
                </div>
                <StatusBadge tone="concept">PROPOSED COLLABORATION</StatusBadge>
              </div>
              <div className="mt-4 text-[18px] font-semibold" style={{ color: "#fff" }}>SUQ Communication</div>
              <div className="mt-1 text-[14px]" style={{ color: P.ink }}>Teresa Scacchi</div>
              <div className="mt-1 text-[11.5px]" style={{ color: P.silver }}>
                Jornalista · Travel Designer · Consultora de Comunicação
              </div>
              <div className="mt-5 text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.silver }}>
                Posicionamento
              </div>
              <p className="mt-2 text-[12.5px] leading-relaxed" style={{ color: P.dim }}>
                Potencial apoio externo de Acesso a Mercados &amp; Comunicação para o piloto de validação em Itália.
              </p>
              <div className="mt-5 text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.silver }}>
                Experiência Apresentada na Proposta
              </div>
              <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[12px]" style={{ color: P.dim }}>
                {[
                  "Relação profissional de longa data com Cabo Verde",
                  "Experiência no mercado turístico italiano",
                  "Experiência de comunicação com Tour Operators",
                  "Promoção de destinos",
                  "Jornalismo de viagens",
                  "Comunicação B2B",
                  "Relações institucionais e com media",
                ].map((x) => (
                  <li key={x} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: P.cyan }} />{x}</li>
                ))}
              </ul>
              <p className="mt-5 text-[11px] italic p-3 rounded-md" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}`, color: P.dim }}>
                Nenhum contrato foi assinado. Trata-se de uma proposta recebida e em avaliação institucional. A SUQ Communication não é apresentada como parceiro oficial da BSGOTECH.
              </p>
            </div>

            {/* Future Synergy — Tourism Guide */}
            <div className="rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.purple}66` }} data-testid="cv-future-synergy">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.purple }}>
                  Sinergia Futura
                </div>
                <StatusBadge tone="future">POTENTIAL FUTURE SYNERGY</StatusBadge>
              </div>
              <div className="mt-4 text-[18px] font-semibold" style={{ color: "#fff" }}>Guia Turístico de Cabo Verde</div>
              <div className="mt-1 text-[13px]" style={{ color: P.ink }}>Impresso + Digital</div>
              <div className="mt-1 text-[11.5px]" style={{ color: P.silver }}>Estado: Em desenvolvimento por Teresa Scacchi</div>
              <div className="mt-5 flex items-start gap-2">
                <BookOpen size={16} style={{ color: P.purple }} className="mt-0.5 shrink-0" />
                <p className="text-[12.5px] leading-relaxed" style={{ color: P.dim }}>
                  Um projecto de guia turístico atualmente em desenvolvimento poderá criar oportunidades futuras de conteúdo de destinos, visibilidade e posicionamento em mercados internacionais.
                </p>
              </div>
              <p className="mt-5 text-[11px] italic p-3 rounded-md" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}`, color: P.dim }}>
                A Eco Collection não é apresentada como participante oficial do guia. Qualquer colaboração seria discutida separadamente e depende de alinhamento mútuo.
              </p>
            </div>
          </div>

          {/* Institutional / Market Access Ask */}
          <div className="mt-10 rounded-2xl p-6 lg:p-8" style={{ background: `linear-gradient(135deg, ${P.bg2} 0%, rgba(201,162,74,0.06) 100%)`, border: `1px solid ${P.gold}44` }} data-testid="cv-ma-ask">
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.gold }}>O Pedido</div>
            <h3 className="mt-3 font-semibold tracking-tight text-[24px] sm:text-[28px] max-w-3xl" style={{ color: "#fff", letterSpacing: "-0.02em" }}>
              Construir o <span style={{ color: P.gold }}>modelo certo de acesso a mercados.</span>
            </h3>
            <p className="mt-4 max-w-3xl text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
              A Eco Collection está actualmente a avaliar como combinar tecnologia, oferta turística local e distribuição B2B internacional através de uma abordagem controlada de validação de mercado.
            </p>
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                "Orientação institucional",
                "Conexões no ecossistema turístico",
                "Apoio à internacionalização",
                "Acesso a mercados B2B",
                "Validação de piloto",
                "Instrumentos de apoio / cofinanciamento",
              ].map((k) => (
                <div key={k} className="rounded-md p-3 text-[12px] font-medium" style={{ background: `${P.gold}0d`, border: `1px solid ${P.gold}33`, color: P.ink }}>
                  {k}
                </div>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="mailto:admin@bsgotech.com?subject=Eco%20Collection%20%C2%B7%20Acesso%20a%20Mercados"
                 onClick={() => track("market_access_discuss_clicked")}
                 data-testid="cv-ma-cta-discuss"
                 className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
                 style={{ background: P.gold, color: "#0d0a02" }}>
                Discutir Acesso a Mercados <ArrowUpRight size={13} />
              </a>
              <a href="#institutional" onClick={scrollTo("institutional")}
                 data-testid="cv-ma-cta-presentation"
                 className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
                 style={{ background: `${P.cyan}18`, color: P.cyan, border: `1px solid ${P.cyan}55` }}>
                Ver Apresentação Institucional <FileText size={13} />
              </a>
              <a href="/downloads/BSGOTECH_Eco_Collection_Italy_Market_Access_2026.pdf"
                 target="_blank" rel="noopener noreferrer" download
                 onClick={() => track("market_access_deck_downloaded")}
                 data-testid="cv-ma-cta-download"
                 className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
                 style={{ background: P.glass, color: P.ink, border: `1px solid ${P.border}` }}>
                Descarregar Deck · PDF <Download size={13} />
              </a>
            </div>
          </div>

          {/* Governance disclaimer */}
          <p className="mt-6 text-[11.5px] italic leading-relaxed p-4 rounded-md" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}`, color: P.dim }} data-testid="cv-ma-governance">
            Iniciativa institucional de validação de mercado. A Eco Collection está LIVE e em validação. O Acesso ao Mercado Italiano é um piloto de 90 dias proposto e atualmente em avaliação. A SUQ Communication (Teresa Scacchi) é uma colaboração proposta — nenhum contrato foi assinado. O Guia Turístico de Cabo Verde é uma potencial sinergia futura, em desenvolvimento por Teresa Scacchi — a Eco Collection não é apresentada como participante oficial. Não está implícito qualquer aval governamental, financiamento público, contrato assinado com Tour Operators, venda turística garantida ou investimento garantido.
          </p>
        </div>
      </section>

      {/* ============ SECTION 05 · NU BAI ============ */}
      <section id="nubai" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border }}
               onMouseEnter={() => track("nubai_section_viewed")}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-3 flex-wrap justify-between">
            <SectionHeader eyebrow="NEXIS · Nu Bai · Digital Mobility & Services"
              title="From Digital Prototype to Controlled Mobility Pilot"
              subtitle="Uma camada operacional comum entre passageiros, operadores e serviços — pensada para pilotos locais controlados antes de qualquer expansão." />
            <StatusBadge tone="mvp">MVP / PILOT DEVELOPMENT</StatusBadge>
          </div>
          <div className="mt-10">
            <PremiumVisual src="/cabo-verde/visuals/05-nubai-mobility.webp"
              alt="Nu Bai digital mobility MVP concept — one vehicle on a coastal Cabo Verde-style road with abstract GPS route overlay."
              tone="cyan" testid="cv-visual-nubai" />
          </div>
          <div className="mt-12 grid lg:grid-cols-[1.1fr_1fr] gap-8">
            {/* Architecture */}
            <div className="rounded-xl p-6 lg:p-8" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <ol className="space-y-3">
                {[
                  { k: "Passenger",                icon: Users2 },
                  { k: "Nu Bai",                   icon: Network, primary: true },
                  { k: "Local Operator / Driver",  icon: Car },
                  { k: "Mobility Service",         icon: MapPin },
                  { k: "Data & Operational Feedback", icon: Cpu },
                ].map((s, i) => (
                  <li key={s.k} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md flex items-center justify-center"
                         style={{ background: `${P.cyan}${s.primary ? "22" : "12"}`, border: `1px solid ${P.cyan}${s.primary ? "aa" : "44"}` }}>
                      <s.icon size={16} style={{ color: P.cyan }} />
                    </div>
                    <div className="text-[13.5px] font-semibold" style={{ color: s.primary ? "#fff" : P.ink }}>{s.k}</div>
                    {i < 4 && <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${P.cyan}55 0%, transparent 100%)` }} />}
                  </li>
                ))}
              </ol>
            </div>
            {/* Potential use cases */}
            <div className="rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.cyan }}>Potential use cases to validate</div>
              <ul className="mt-4 grid grid-cols-2 gap-3 text-[12.5px]" style={{ color: P.ink }}>
                {["Tourism Mobility","Taxi / Cooperative Integration","Local Transport","Digital Ride Requests","Operator Management","Mobility Data"].map((x) => (
                  <li key={x} className="rounded-md p-3" style={{ background: `${P.cyan}0d`, border: `1px solid ${P.cyan}33` }}>{x}</li>
                ))}
              </ul>
              <p className="mt-5 text-[11.5px] italic leading-relaxed" style={{ color: P.dim }}>
                These are potential use cases requiring local validation. They are not confirmed problems in Cabo Verde.
              </p>
            </div>
          </div>

          {/* Phase 1 development scope */}
          <div className="mt-14">
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.gold }}>Phase 1 · From Prototype to Functional MVP</div>
            <div className="mt-6">
              <PremiumVisual src="/cabo-verde/visuals/06-phase1-architecture.webp"
                alt="Nu Bai Phase 1 MVP architecture — Passenger App, Driver App, Admin Dashboard connected via a clean backend / API architecture diagram."
                tone="cyan" testid="cv-visual-phase1" />
            </div>
            <div className="mt-4 grid md:grid-cols-3 gap-4">
              <div className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.cyan}66` }}>
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.cyan }}>Current</div>
                <div className="mt-2 text-[13px] font-semibold" style={{ color: "#fff" }}>High-Fidelity Prototypes</div>
                <ul className="mt-2 space-y-1 text-[12px]" style={{ color: P.dim }}>
                  <li>Passenger App</li><li>Driver App</li>
                </ul>
              </div>
              <div className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}66` }}>
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.gold }}>Phase 1 development</div>
                <div className="mt-2 text-[13px] font-semibold" style={{ color: "#fff" }}>Functional MVP</div>
                <ul className="mt-2 space-y-1 text-[12px]" style={{ color: P.dim }}>
                  <li>Functional Passenger App</li><li>Functional Driver App</li><li>Admin Dashboard</li>
                  <li>Backend / API · Cloud DB</li><li>Ride Matching · GPS live tracking</li>
                  <li>Notifications · Basic Support</li><li>Payment Integration*</li>
                </ul>
              </div>
              <div className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.green}66` }}>
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.green }}>Controlled pilot</div>
                <div className="mt-2 text-[13px] font-semibold" style={{ color: "#fff" }}>Cabo Verde</div>
                <p className="mt-2 text-[12px]" style={{ color: P.dim }}>Real users · controlled scope · defined KPIs.</p>
              </div>
            </div>
            <p className="mt-4 text-[11px] italic" style={{ color: P.dim }}>
              *Payment integration is subject to availability of an appropriate licensed local provider and required regulatory/commercial approvals.
            </p>
            <div className="mt-4 rounded-md p-3 text-[12px] font-medium"
                 style={{ background: `${P.gold}0d`, border: `1px solid ${P.gold}44`, color: P.ink }}>
              <strong style={{ color: P.gold }}>Current status:</strong> Development scope defined · functional MVP development / validation stage. Phase 1 is not yet completed.
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 07 · CONTROLLED PILOT ============ */}
      <section id="pilot" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border, background: P.bg2 }}>
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="Controlled Pilot"
            title="Start small. Measure. Learn."
            subtitle="Um piloto controlado é a única forma responsável de testar tecnologia em contexto real — sem exagerar promessas nem penalizar utilizadores." />
          <div className="mt-10">
            <PremiumVisual src="/cabo-verde/visuals/07-controlled-pilot.webp"
              alt="Isometric visualization of a small controlled mobility pilot in a Cabo Verde-like coastal urban zone with a defined pilot boundary and a limited number of vehicles."
              tone="gold" testid="cv-visual-pilot" />
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { n: "01", k: "Define",  d: "Problem + Use Case + Stakeholders" },
              { n: "02", k: "Pilot",   d: "Controlled Scope + Real Users" },
              { n: "03", k: "Measure", d: "Usage + Operations + Feedback + Economics" },
              { n: "04", k: "Decide",  d: "Adapt · Stop · Scale" },
            ].map((s) => (
              <div key={s.n} className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}66` }}>
                <div className="text-[10px] uppercase tracking-[0.28em] font-mono font-semibold" style={{ color: P.gold }}>{s.n}</div>
                <div className="mt-2 text-[15px] font-semibold" style={{ color: "#fff" }}>{s.k}</div>
                <div className="mt-1.5 text-[12.5px]" style={{ color: P.dim }}>{s.d}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>Potential Pilot Framework</div>
            <div className="mt-3 grid md:grid-cols-5 gap-3 text-[12.5px]" style={{ color: P.ink }}>
              {["1 Use Case","1 Local Market","Limited Operator Group","Defined Validation Period","Clear KPIs"].map((x) => (
                <div key={x} className="rounded-md p-3 text-center" style={{ background: `${P.gold}0d`, border: `1px solid ${P.gold}33` }}>{x}</div>
              ))}
            </div>
            <p className="mt-4 text-[11.5px] italic" style={{ color: P.dim }}>
              The final pilot scope has not been predetermined. It should be developed with the appropriate Cabo Verdean stakeholders and local partners.
            </p>
          </div>
          <div className="mt-10">
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>Validation KPIs</div>
            <div className="mt-4 rounded-2xl overflow-hidden mb-6" style={{ border: `1px solid ${P.border}` }}>
              <PremiumVisual src="/cabo-verde/visuals/12-pilot-kpi-dashboard.webp"
                alt="Premium data-visualization environment — abstract dashboard cards showing what would be measured during a pilot: adoption, operations, experience, economics, technology."
                tone="cyan" testid="cv-visual-kpi" />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                { k: "Adoption",   v: "Users and participating operators" },
                { k: "Operations", v: "Requests, completion and response time" },
                { k: "Experience", v: "User / operator feedback and repeat usage" },
                { k: "Economics",  v: "Operational costs and transaction economics" },
                { k: "Technology", v: "GPS performance, platform stability and system performance" },
              ].map((k) => (
                <div key={k.k} className="rounded-md p-4" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.gold }}>{k.k}</div>
                  <div className="mt-1.5 text-[12px]" style={{ color: P.dim }}>{k.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 08 · NU BAI PAY ============ */}
      <section id="nubai-pay" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-3 flex-wrap justify-between">
            <SectionHeader eyebrow="Nu Bai · Way pillar · Payments Through Licensed Partners"
              title="Software Payment Layer — Not a Bank"
              subtitle="A camada WAY do ecossistema Nu Bai é um layer de integração de pagamentos. As transações financeiras dependem de provedores locais devidamente licenciados." />
            <StatusBadge tone="concept">CONCEPT / REGULATORY VALIDATION</StatusBadge>
          </div>
          <div className="mt-10">
            <PremiumVisual src="/cabo-verde/visuals/10-nubai-pay-architecture.webp"
              alt="Nu Bai Pay architecture — software integration layer routing to a licensed financial partner for regulated execution."
              tone="purple" testid="cv-visual-nubai-pay" />
          </div>
          <div className="mt-12 grid lg:grid-cols-[1.1fr_1fr] gap-8">
            <div className="rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <ol className="space-y-3">
                {[
                  { k: "Nu Bai",                       icon: Network },
                  { k: "Nu Bai · Way (Integration Layer)", icon: Layers, primary: true },
                  { k: "Licensed PSP / Fintech / Bank",  icon: Landmark },
                  { k: "Payment Transaction",            icon: ShieldCheck },
                ].map((s, i) => (
                  <li key={s.k} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md flex items-center justify-center"
                         style={{ background: `${P.purple}${s.primary ? "22" : "12"}`, border: `1px solid ${P.purple}${s.primary ? "aa" : "44"}` }}>
                      <s.icon size={16} style={{ color: P.purple }} />
                    </div>
                    <div className="text-[13.5px] font-semibold" style={{ color: s.primary ? "#fff" : P.ink }}>{s.k}</div>
                    {i < 3 && <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${P.purple}55 0%, transparent 100%)` }} />}
                  </li>
                ))}
              </ol>
            </div>
            <div className="grid gap-4">
              <div className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.purple}66` }}>
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.purple }}>BSGOTECH</div>
                <ul className="mt-2 space-y-1 text-[12.5px]" style={{ color: P.dim }}>
                  <li>Technology</li><li>User Experience</li><li>Software Integration</li><li>Transaction Workflow</li>
                </ul>
              </div>
              <div className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}66` }}>
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.gold }}>Licensed Financial Partner</div>
                <ul className="mt-2 space-y-1 text-[12.5px]" style={{ color: P.dim }}>
                  <li>Payment Execution</li><li>Custody</li><li>KYC / AML</li><li>Financial Compliance</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-8 rounded-xl p-6" style={{ background: `${P.purple}0e`, border: `1px solid ${P.purple}55`, color: P.ink }}>
            <p className="text-[13px] leading-relaxed">
              <strong style={{ color: P.purple }}>Prominent disclosure:</strong> The Nu Bai Way pillar is currently a proposed software payment layer. It is not an independently licensed financial institution or payment provider. Live financial transactions depend on licensed local providers (banks, fintechs or PSPs), commercial agreements and regulatory validation.
            </p>
          </div>
        </div>
      </section>

      {/* ============ SECTION 08b · NU BAI POINT ============ */}
      <section id="nubai-point" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border, background: `linear-gradient(180deg, ${P.bg2} 0%, ${P.bg} 100%)` }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-3 flex-wrap justify-between">
            <SectionHeader eyebrow="Nu Bai · Point pillar · Proximity Infrastructure"
              title="Financial & Digital Services Closer to Communities"
              subtitle="A proposed proximity infrastructure concept designed for Cabo Verde, connecting communities with digital, mobility and potentially financial services through authorized partners." />
            <StatusBadge tone="concept">CONCEPT · SUBJECT TO PARTNERSHIP & REGULATORY APPROVAL</StatusBadge>
          </div>

          {/* Hero visual — modular proximity kiosk in coastal Cabo Verde */}
          <div className="mt-10">
            <PremiumVisual src="/cabo-verde/visuals/16-nubai-point.webp"
              alt="Concept visualization of a compact modular Nu Bai Point proximity kiosk in a coastal Cabo Verde town, with subtle MOVE, PAY and FUTURE pillar icons floating above."
              tone="green" testid="cv-visual-nubai-point" />
          </div>

          {/* How it works inside */}
          <div className="mt-12 grid lg:grid-cols-[1.15fr_1fr] gap-8 items-center" data-testid="nbp-interior-block">
            <PremiumVisual src="/cabo-verde/visuals/18-nubai-point-interior.webp"
              alt="Interior of a modular Nu Bai Point — self-service touchscreen terminals on one side, a compact assistance counter with a curved digital wall display on the other side, warm minimalist finishes and a coastal Cabo Verde view at golden hour. No people."
              tone="cyan" testid="cv-visual-nubai-point-interior" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>How It Works Inside</div>
              <h3 className="mt-3 font-semibold tracking-tight text-[22px] sm:text-[26px]" style={{ color: "#fff", letterSpacing: "-0.02em" }}>
                Digital when possible.<br /><span style={{ color: P.cyan }}>Human when needed.</span>
              </h3>
              <p className="mt-4 text-[13.5px] leading-relaxed" style={{ color: P.dim }}>
                Each Nu Bai Point combines self-service technology and a compact assistance layer, so communities can access digital, mobility and (through authorized partners) financial services without needing a traditional bank branch.
              </p>
              <ul className="mt-5 grid grid-cols-1 gap-2 text-[12.5px]">
                {[
                  { c: P.cyan,   k: "Self-service terminals",  d: "Touchscreen kiosks for common operations — QR payments, service payments, top-ups, mobility bookings." },
                  { c: P.green,  k: "Assistance layer",         d: "Staffed counter for onboarding, guidance and exception handling — human help when technology alone is not enough." },
                  { c: P.purple, k: "Secure connectivity",      d: "Encrypted connection to the Nu Bai platform and to the authorized financial partner — no funds held on-site." },
                  { c: P.gold,   k: "Modular footprint",        d: "Compact, prefabricated architecture that can be deployed to different community contexts with minimal civil works." },
                ].map((f) => (
                  <li key={f.k} className="rounded-md p-3" style={{ background: `${f.c}0d`, border: `1px solid ${f.c}33` }}>
                    <div className="flex items-start gap-2">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: f.c }} />
                      <div>
                        <div className="text-[12.5px] font-semibold" style={{ color: "#fff" }}>{f.k}</div>
                        <div className="mt-0.5 text-[11.5px]" style={{ color: P.dim }}>{f.d}</div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-[11px] italic" style={{ color: P.dim }}>
                Illustrative interior. Final architecture, layout and equipment depend on partner requirements, safety norms and pilot validation.
              </p>
            </div>
          </div>

          {/* Three pillars */}
          <div className="mt-12 grid md:grid-cols-3 gap-4" data-testid="nbp-pillars">
            {[
              { k: "move",   c: P.green,  code: "MOVE",   title: "Mobility",         href: "#nubai",        items: ["Ride & mobility assistance", "Customer information", "Access to the Nu Bai ecosystem"] },
              { k: "way",    c: P.cyan,   code: "WAY",    title: "Nu Bai Way",       href: "#nubai-pay",    items: ["QR payments", "Service payments & top-ups", "Bill and utility payments*", "Always subject to a financial partner and regulatory framework"] },
              { k: "future", c: P.purple, code: "FUTURE", title: "Nu Bai Future",    href: "#nubai-future", items: ["Goal-based savings — Education, First Home, Emergency, Small Business", "Future Kids goals", "Family contributions from Cabo Verde and the diaspora"] },
            ].map((p) => (
              <div key={p.k} className="rounded-xl p-5 flex flex-col" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${p.c}88` }} data-testid={`nbp-pillar-${p.k}`}>
                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-[10px] uppercase tracking-[0.28em] font-semibold self-start" style={{ background: `${p.c}18`, border: `1px solid ${p.c}55`, color: p.c }}>
                  {p.code}
                </div>
                <div className="mt-3 text-[16px] font-semibold" style={{ color: "#fff" }}>{p.title}</div>
                <ul className="mt-3 space-y-1.5 text-[12.5px] flex-1" style={{ color: P.dim }}>
                  {p.items.map((x) => (<li key={x} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: p.c }} />{x}</li>))}
                </ul>
                <a href={p.href} onClick={scrollTo(p.href.replace("#",""))}
                   className="mt-4 inline-flex items-center gap-1.5 self-start text-[11px] font-semibold uppercase tracking-[0.2em]"
                   style={{ color: p.c }}
                   data-testid={`nbp-pillar-${p.k}-link`}>
                  See section <ChevronRight size={12} />
                </a>
              </div>
            ))}
          </div>

          {/* Architecture */}
          <div className="mt-8 rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>How Money Flows — Regulated Path</div>
            <ol className="mt-4 grid md:grid-cols-4 gap-3 items-stretch">
              {[
                { k: "Authorized Financial Partner", d: "Bank · PSP · Fintech (KYC, custody, settlement, compliance)", icon: Landmark, c: P.gold },
                { k: "Nu Bai Platform",              d: "Technology, integration and workflow orchestration",         icon: Network,  c: P.cyan },
                { k: "Nu Bai Point",                 d: "Physical proximity access + customer assistance",             icon: Building2, c: P.green },
                { k: "Community",                    d: "End users benefit from closer digital + financial access",    icon: Users2,   c: P.purple },
              ].map((s, i) => (
                <li key={s.k} className="rounded-lg p-4" style={{ background: `${s.c}08`, border: `1px solid ${s.c}33` }}>
                  <s.icon size={16} style={{ color: s.c }} />
                  <div className="mt-2 text-[10px] uppercase tracking-[0.22em] font-mono font-semibold" style={{ color: s.c }}>Step {i+1}</div>
                  <div className="mt-1 text-[13px] font-semibold" style={{ color: "#fff" }}>{s.k}</div>
                  <div className="mt-1 text-[11.5px]" style={{ color: P.dim }}>{s.d}</div>
                </li>
              ))}
            </ol>
            <div className="mt-4 text-[11.5px] italic" style={{ color: P.dim }}>
              BSGOTECH / Nu Bai provides the technology, physical infrastructure and user experience. Nu Bai Point does NOT hold customer funds.
            </div>
          </div>

          {/* IS / IS NOT */}
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <div className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.green}55`, borderTop: `2px solid ${P.green}` }}>
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.green }}>Nu Bai Point IS</div>
              <ul className="mt-3 space-y-1.5 text-[12.5px]" style={{ color: P.ink }}>
                {["Technology infrastructure","Physical proximity point","Customer assistance layer","Potential distribution channel for authorized partners"].map((x) => (<li key={x} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: P.green }} />{x}</li>))}
              </ul>
            </div>
            <div className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.purple}66`, borderTop: `2px solid ${P.purple}` }} data-testid="nbp-isnot">
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.purple }}>Nu Bai Point IS NOT</div>
              <ul className="mt-3 space-y-1.5 text-[12.5px]" style={{ color: P.ink }}>
                {["A bank","An independent financial institution","Custodian of customer funds","A replacement for existing banks"].map((x) => (<li key={x} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: P.purple }} />{x}</li>))}
              </ul>
            </div>
          </div>

          {/* Pilot strategy */}
          <div className="mt-10">
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>Pilot Strategy · Scale Only After Evidence</div>
            <div className="mt-4 grid md:grid-cols-5 gap-3">
              {[
                { n: "01", k: "1 Controlled Point", d: "Location to be validated with partners — not pre-selected." },
                { n: "02", k: "6-Month Pilot",       d: "Real usage with controlled scope and clear KPIs." },
                { n: "03", k: "Data & Audit",        d: "Demand, ops costs, UX, tech stability, partner integration, unit economics." },
                { n: "04", k: "GO / ADAPT / STOP",   d: "Decision based on evidence — scaling is not the default outcome." },
                { n: "05", k: "Controlled Expansion", d: "Only after validation. Phased and partner-aligned." },
              ].map((s) => (
                <div key={s.n} className="rounded-xl p-4" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}55` }}>
                  <div className="text-[10px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: P.gold }}>{s.n}</div>
                  <div className="mt-2 text-[13px] font-semibold" style={{ color: "#fff" }}>{s.k}</div>
                  <div className="mt-1.5 text-[11.5px]" style={{ color: P.dim }}>{s.d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Ecosystem long-term integration */}
          <div className="mt-10 rounded-xl p-6" style={{ background: `${P.cyan}0a`, border: `1px solid ${P.cyan}44` }}>
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.cyan }}>Nu Bai Ecosystem · Cabo Verde (long-term vision)</div>
            <div className="mt-3 grid md:grid-cols-4 gap-2 text-[12px]">
              {[
                ["MOVE", "Mobility",             P.green],
                ["PAY", "Payments",              P.cyan],
                ["FUTURE", "Savings & Family Goals", P.purple],
                ["POINT", "Physical Access Layer", P.gold],
              ].map(([code, label, c]) => (
                <div key={code} className="rounded-md p-3 text-center" style={{ background: `${c}12`, border: `1px solid ${c}44` }}>
                  <div className="text-[10px] uppercase tracking-[0.28em] font-mono font-semibold" style={{ color: c }}>{code}</div>
                  <div className="mt-1 text-[12.5px] font-semibold" style={{ color: "#fff" }}>{label}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[13px] italic" style={{ color: P.ink }}>
              “Nu Bai is the digital ecosystem. Nu Bai Point brings that ecosystem closer to communities.”
            </p>
          </div>

          {/* Regulatory disclaimer */}
          <p className="mt-6 text-[11.5px] italic leading-relaxed p-4 rounded-md" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}`, color: P.dim }} data-testid="nbp-disclaimer">
            Concept under development. Financial services and functionalities are subject to partnerships, feasibility assessment and applicable regulatory approval. No banks, PSPs, investors, Government of Cabo Verde or other institutions are represented as confirmed partners. The Nu Bai Way, Nu Bai Future and Nu Bai Point pillars do not hold a financial license. No market, user, transaction or revenue figures are implied.
          </p>

          {/* Final tagline */}
          <div className="mt-10 text-center">
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>Nu Bai</div>
            <div className="mt-3 text-[26px] sm:text-[32px] font-semibold" style={{ color: "#fff", letterSpacing: "-0.02em" }}>
              Move. Pay. <span style={{ color: P.purple }}>Build Your Future.</span>
            </div>
            <div className="mt-3 text-[13px]" style={{ color: P.dim }}>Digital when possible. Human when needed.</div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.28em]" style={{ color: P.gold }}>Powered by BSGOTECH</div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 08c · NU BAI FUTURE ============ */}
      <section id="nubai-future" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-3 flex-wrap justify-between">
            <SectionHeader eyebrow="Nu Bai · Future pillar · Family & Financial Goals"
              title="Goal-based Savings for Cabo Verdean Families"
              subtitle="Nu Bai Future é um conceito de poupança orientada a objectivos — educação, primeira casa, emergência, pequeno negócio — com contribuições da família em Cabo Verde e da diáspora, sempre através de parceiros financeiros licenciados." />
            <StatusBadge tone="concept">EARLY CONCEPT · SUBJECT TO PARTNERSHIP & REGULATORY APPROVAL</StatusBadge>
          </div>

          {/* Product mock — phone + goal + family contributions */}
          <div className="mt-10 rounded-2xl p-6 lg:p-8" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.purple}66` }} data-testid="nbf-mock">
            <div className="grid lg:grid-cols-[0.85fr_1fr_1.1fr] gap-8 items-center">
              {/* Phone mockup */}
              <div className="flex justify-center">
                <img src="/cabo-verde/visuals/17-nubai-future-phone.webp"
                     alt="Nu Bai Future smartphone mockup — Future Kids · Sofia · University goal €15,000, monthly €50, illustrative 22% progress and family contribution avatars (Father, Mother, Grandmother, Family EU)."
                     loading="lazy" decoding="async"
                     className="w-full max-w-[300px] rounded-2xl"
                     data-testid="cv-visual-future-phone" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.purple }}>Goal-based Savings</div>
                <div className="mt-3 text-[16px] font-semibold" style={{ color: "#fff" }}>Future Kids — Sofia</div>
                <div className="mt-3 space-y-2 text-[12.5px]" style={{ color: P.dim }}>
                  <div className="flex justify-between"><span>Goal</span><span style={{ color: P.ink }}>University</span></div>
                  <div className="flex justify-between"><span>Target</span><span style={{ color: P.ink }}>€15,000</span></div>
                  <div className="flex justify-between"><span>Monthly contribution</span><span style={{ color: P.ink }}>€50</span></div>
                </div>
                <div className="mt-4 h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <div className="h-full rounded-full" style={{ width: "22%", background: `linear-gradient(90deg, ${P.purple} 0%, ${P.cyan} 100%)` }} />
                </div>
                <div className="mt-1.5 text-[11px]" style={{ color: P.dim }}>Illustrative progress · not a guarantee of return.</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>Future Family Contributions (concept)</div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-[12.5px]">
                  {[
                    ["Father · Cabo Verde", "€50 / mo"],
                    ["Mother · Cabo Verde", "€50 / mo"],
                    ["Grandmother · Cabo Verde", "€20 / mo"],
                    ["Family · Portugal / Europe", "€25 / mo"],
                  ].map(([who, amt]) => (
                    <div key={who} className="rounded-md p-3" style={{ background: `${P.purple}0d`, border: `1px solid ${P.purple}33`, color: P.ink }}>
                      <div>{who}</div>
                      <div className="text-[11px]" style={{ color: P.silver }}>{amt}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-[11px] italic" style={{ color: P.dim }}>
                  Contributions from Cabo Verde and the diaspora would flow through authorized financial channels. No returns are implied or guaranteed.
                </div>
              </div>
            </div>
          </div>

          {/* Goal categories */}
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="nbf-goals">
            {[
              { k: "Education",      d: "Save monthly for children's school, university or vocational training." },
              { k: "First Home",     d: "Family-supported goal to build initial capital for the first home." },
              { k: "Emergency Fund", d: "A small dedicated buffer for unexpected household or health needs." },
              { k: "Small Business", d: "Structured savings towards a family-run business or independent activity." },
            ].map((g) => (
              <div key={g.k} className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.purple}66` }}>
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.purple }}>Goal</div>
                <div className="mt-2 text-[14px] font-semibold" style={{ color: "#fff" }}>{g.k}</div>
                <div className="mt-1.5 text-[12px] leading-relaxed" style={{ color: P.dim }}>{g.d}</div>
              </div>
            ))}
          </div>

          {/* IS / IS NOT */}
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            <div className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.green}55`, borderTop: `2px solid ${P.green}` }}>
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.green }}>Nu Bai Future IS</div>
              <ul className="mt-3 space-y-1.5 text-[12.5px]" style={{ color: P.ink }}>
                {[
                  "A goal-based savings concept",
                  "A software layer for setting and tracking family goals",
                  "Dependent on authorized financial partners for money custody and execution",
                  "Transparent about progress and family contributions",
                ].map((x) => (<li key={x} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: P.green }} />{x}</li>))}
              </ul>
            </div>
            <div className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.purple}66`, borderTop: `2px solid ${P.purple}` }} data-testid="nbf-isnot">
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.purple }}>Nu Bai Future IS NOT</div>
              <ul className="mt-3 space-y-1.5 text-[12.5px]" style={{ color: P.ink }}>
                {[
                  "A bank or an independent financial institution",
                  "An investment product with implied returns",
                  "A custodian of customer funds",
                  "A promise of any specific outcome",
                ].map((x) => (<li key={x} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: P.purple }} />{x}</li>))}
              </ul>
            </div>
          </div>

          {/* Regulatory disclaimer */}
          <p className="mt-8 text-[11.5px] italic leading-relaxed p-4 rounded-md" style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${P.border}`, color: P.dim }} data-testid="nbf-disclaimer">
            Nu Bai Future is currently an early concept. Any actual savings, custody, transfers or investment activity depends on partnerships with duly licensed financial institutions (banks, fintechs or PSPs), applicable regulatory approval in Cabo Verde and the countries of contributing families, and validated commercial agreements. No specific returns are offered or implied. Data shown above is illustrative only.
          </p>
        </div>
      </section>

      {/* ============ SECTION 09 · BSGOTECH DIGITAL ============ */}
      <section id="digital" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border, background: P.bg2 }}>
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="NEXIS · Technology & Digital"
            title="Four Capability Blocks"
            subtitle="A NEXIS é a divisão de Tecnologia & Digital da BSGOTECH — camada de integração entre produtos, dados, hardware seleccionado e operações." />
          <div className="mt-10">
            <PremiumVisual src="/cabo-verde/visuals/11-bsgotech-digital.webp"
              alt="BSGOTECH Digital integration ecosystem — central core connected to Web, Mobile, AI, Cloud, APIs, Dashboards, generic Digital Hardware and Edge Devices."
              tone="cyan" testid="cv-visual-digital" />
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Layers,  title: "Platforms",       d: "Web · Mobile · Business Systems" },
              { icon: Cpu,     title: "AI & Automation", d: "Digital Workflows · AI-Assisted Processes" },
              { icon: Network, title: "Data & Integration", d: "APIs · Dashboards · Connectivity" },
              { icon: Boxes,   title: "Digital Hardware", d: "Selected international technology hardware integrated according to project requirements." },
            ].map((b, i) => (
              <div key={i} className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.cyan}55` }}>
                <div className="w-10 h-10 rounded-md flex items-center justify-center"
                     style={{ background: `${P.cyan}14`, border: `1px solid ${P.cyan}55` }}>
                  <b.icon size={18} style={{ color: P.cyan }} />
                </div>
                <div className="mt-3 text-[13px] font-semibold" style={{ color: "#fff" }}>{b.title}</div>
                <div className="mt-1.5 text-[12px] leading-relaxed" style={{ color: P.dim }}>{b.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECTION 10 · HX ECOSYSTEMS ============ */}
      <section id="hx" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-start gap-3 flex-wrap justify-between">
            <SectionHeader eyebrow="HX Ecosystems · Integrated Infrastructure"
              title="Physical + Digital Integration — Future Roadmap"
              subtitle="Integrated infrastructure concepts subject to local feasibility, technical partnerships, regulatory requirements and pilot validation." />
            <StatusBadge tone="future">FUTURE ROADMAP</StatusBadge>
          </div>
          <div className="mt-10">
            <PremiumVisual src="/cabo-verde/visuals/08-hx-future-roadmap.webp"
              alt="Concept visualization of a small integrated clean-energy pilot site on a Cabo Verde-style island — solar, battery storage, EV charging, water technology and digital connectivity."
              tone="gold" testid="cv-visual-hx" />
          </div>
          <div className="mt-12 relative">
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { icon: Sun,     k: "HX Solar" },
                { icon: Waves,   k: "HX Water" },
                { icon: Zap,     k: "Energy Storage" },
                { icon: Car,     k: "E-Mobility" },
                { icon: Building2, k: "Modular" },
                { icon: Cpu,     k: "Digital" },
              ].map((n) => (
                <div key={n.k} className="rounded-xl p-5 text-center"
                     style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}44` }}>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center mx-auto"
                       style={{ background: `${P.gold}12`, border: `1px solid ${P.gold}55` }}>
                    <n.icon size={18} style={{ color: P.gold }} />
                  </div>
                  <div className="mt-3 text-[12.5px] font-semibold" style={{ color: "#fff" }}>{n.k}</div>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>Long-term Integrated Vision</div>
              <div className="mt-3">
                <PremiumVisual src="/cabo-verde/visuals/13-longterm-vision.webp"
                  alt="Long-term integrated Cabo Verde island vision — tourism, mobility hub, small solar+battery pilot, modular structure and water technology connected via a subtle digital layer. Conceptual, phased, not deployed."
                  tone="gold" testid="cv-visual-longterm" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 11 · TRANSPARENCY ============ */}
      <section className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border, background: P.bg2 }}>
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="Transparency by Design" title="Where We Are Today"
            subtitle="Distinguimos claramente as capacidades actuais das desenvolvimentos futuros." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { tone: "live",    band: "Live / Validation",              item: "Eco Collection" },
              { tone: "mvp",     band: "MVP / Pilot Development",         item: "Nu Bai" },
              { tone: "concept", band: "Concept / Regulatory Validation", item: "Nu Bai · Way pillar" },
              { tone: "future",  band: "Future Roadmap",                  item: "HX Ecosystems" },
            ].map((l, i) => (
              <div key={i} className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
                <StatusBadge tone={l.tone}>{l.band}</StatusBadge>
                <div className="mt-3 text-[16px] font-semibold" style={{ color: "#fff" }}>{l.item}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[13px] italic max-w-2xl" style={{ color: P.dim }}>
            “We distinguish current capabilities from future development.”
          </p>
        </div>
      </section>

      {/* ============ SECTION 12 · PARTNERS ============ */}
      <section className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border }}>
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="Partnership Ecosystem" title="Built With Local Partners"
            subtitle="BSGOTECH does not seek to replace the local ecosystem. Our objective is to integrate with it." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              "Government & Institutions", "Tourism Operators", "Mobility Operators",
              "Banks / Fintech / PSP", "Technology Partners", "Energy & Infrastructure",
              "Universities & Innovation Ecosystem",
            ].map((c) => (
              <div key={c} className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
                <Handshake size={16} style={{ color: P.gold }} />
                <div className="mt-3 text-[13px] font-semibold" style={{ color: "#fff" }}>{c}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECTION 13 · DOCUMENTS ============ */}
      <DocumentsSection />

      {/* ============ SECTION 14 · ROADMAP ============ */}
      <section className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border }}>
        <div className="max-w-7xl mx-auto">
          <SectionHeader eyebrow="Cabo Verde Roadmap" title="From Dialogue to Validation" />
          <div className="mt-10">
            <PremiumVisual src="/cabo-verde/visuals/15-roadmap-journey.webp"
              alt="Six-checkpoint strategic journey — Dialogue, Discovery, Partners, Pilot Design, Validation and a final Decision node forking into Adapt · Stop · Scale."
              tone="cyan" testid="cv-visual-roadmap" />
          </div>
          <div className="mt-12 grid md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { n: "01", k: "Dialogue",     d: "Institutional engagement" },
              { n: "02", k: "Discovery",    d: "Problem identification" },
              { n: "03", k: "Partners",     d: "Local stakeholder alignment" },
              { n: "04", k: "Pilot Design", d: "Scope + responsibilities + KPIs" },
              { n: "05", k: "Validation",   d: "Real-world testing" },
              { n: "06", k: "Decision",     d: "Adapt · Stop · Scale" },
            ].map((s) => (
              <div key={s.n} className="rounded-xl p-4" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}66` }}>
                <div className="text-[10px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: P.gold }}>{s.n}</div>
                <div className="mt-2 text-[13px] font-semibold" style={{ color: "#fff" }}>{s.k}</div>
                <div className="mt-1 text-[11.5px]" style={{ color: P.dim }}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECTION 15 · THE ASK ============ */}
      <section className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border, background: P.bg2 }}>
        <div className="max-w-5xl mx-auto text-center">
          <SectionHeader align="center" eyebrow="The Ask"
            title="What could we validate together?"
            subtitle="Nesta fase, a BSGOTECH procura compreender onde as nossas capacidades podem responder a necessidades reais em Cabo Verde, quais stakeholders devem participar e se existe mérito em estruturar uma primeira validação controlada." />
          <div className="mt-10 grid md:grid-cols-3 gap-3">
            {[
              { icon: Waves,   k: "Digital Tourism",    v: "Eco Collection" },
              { icon: Car,     k: "Digital Mobility",   v: "Nu Bai" },
              { icon: Boxes,   k: "Future Integration", v: "Digital + Infrastructure" },
            ].map((a, i) => (
              <div key={i} className="rounded-xl p-5" style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}66` }}>
                <a.icon size={18} style={{ color: P.gold }} className="mx-auto" />
                <div className="mt-3 text-[11.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.gold }}>{a.k}</div>
                <div className="mt-1 text-[14px] font-semibold" style={{ color: "#fff" }}>{a.v}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-[11.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>
            Guidance · Problem Validation · Local Partners · Pilot Pathway
          </div>
        </div>
      </section>

      {/* ============ SECTION 16 · CONTACT ============ */}
      <section id="contact" className="relative py-28 px-6 lg:px-12 border-t overflow-hidden" style={{ borderColor: P.border }}>
        <img src="/cabo-verde/visuals/09-final-cta.webp" aria-hidden="true"
             className="absolute inset-0 w-full h-full object-cover opacity-40 pointer-events-none" alt=""
             data-testid="cv-visual-final" />
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: `linear-gradient(180deg, rgba(5,8,19,0.55) 0%, rgba(5,8,19,0.85) 60%, rgba(5,8,19,0.96) 100%)` }} />
        <div className="relative max-w-5xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.gold }}>BSGOTECH × Cabo Verde</div>
          <h2 className="mt-4 font-semibold tracking-tight text-[38px] sm:text-[52px] lg:text-[60px] leading-[1.02]"
              style={{ color: "#fff", letterSpacing: "-0.03em" }}>
            Start small.<br />Validate locally.<br /><span style={{ color: P.gold }}>Scale what works.</span>
          </h2>
          <div className="mt-12 grid md:grid-cols-[1.2fr_1fr] gap-8">
            <div className="rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>Founder · BSGOTECH</div>
              <div className="mt-2 text-[22px] font-semibold" style={{ color: "#fff" }}>Cleiton Barreto dos Santos</div>
              <div className="mt-4 flex flex-col gap-1 items-start">
                <a href="mailto:admin@bsgotech.com" onClick={() => track("cabo_verde_contact_clicked")}
                   className="inline-flex items-center gap-2 text-[13px]" style={{ color: P.gold }}>
                  <Mail size={14} /> admin@bsgotech.com
                </a>
                <a href="https://stp.bsgotech.com" target="_blank" rel="noopener noreferrer"
                   onClick={() => track("stp_bsgotech_opened")}
                   data-testid="cv-stp-link"
                   className="inline-flex items-center gap-1.5 text-[13px] transition hover:brightness-125"
                   style={{ color: P.gold }}>
                  stp.bsgotech.com <ArrowUpRight size={12} />
                </a>
              </div>
              <div className="mt-4 text-[11.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.silver }}>
                São Tomé and Príncipe ↔ Cabo Verde ↔ Europe
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="mailto:admin@bsgotech.com" onClick={() => track("cabo_verde_contact_clicked")}
                   className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
                   style={{ background: P.gold, color: "#0d0a02" }}>
                  Contact BSGOTECH <ArrowUpRight size={13} />
                </a>
                <a href="#documents" onClick={scrollTo("documents")}
                   className="inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
                   style={{ background: P.glass, color: P.ink, border: `1px solid ${P.border}` }}>
                  View Strategic Presentation <ChevronRight size={13} />
                </a>
              </div>
            </div>
            <RequestAccessForm />
          </div>
        </div>
      </section>

      <footer className="py-10 text-center border-t" style={{ borderColor: P.border }}>
        <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: P.silver }}>
          BSGOTECH · Strategic Initiative 2026 · Cabo Verde
        </div>
        <div className="mt-2 text-[11px]" style={{ color: P.dim }}>
          © BSGOTECH. Concept material for institutional dialogue.
        </div>
        <div className="mt-4 flex items-center justify-center gap-4 text-[10px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>
          <Link to="/" data-testid="cv-footer-bsgotech" className="opacity-70 hover:opacity-100 transition">BSGOTECH</Link>
          <span style={{ opacity: 0.3 }}>·</span>
          <Link to="/ops" data-testid="cv-footer-ops" className="opacity-60 hover:opacity-100 transition" title="Internal Operations Center">Ops</Link>
        </div>
      </footer>
    </div>
  );
}

/* ============ Sticky top navigation (desktop + mobile) ============ */
function StickyNav({ activeNav }) {
  const [openDrop, setOpenDrop] = React.useState(null);   // desktop dropdown
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [mobileGroup, setMobileGroup] = React.useState(null);
  const closeTimer = React.useRef(null);

  const handleEnter = (id) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpenDrop(id);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpenDrop(null), 140);
  };

  const go = (id) => (e) => {
    e.preventDefault();
    setOpenDrop(null);
    setMobileOpen(false);
    setMobileGroup(null);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Close dropdowns on Escape
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setOpenDrop(null); setMobileOpen(false); setMobileGroup(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="sticky top-0 z-30 backdrop-blur-md" style={{ background: "rgba(5,8,19,0.88)", borderBottom: `1px solid ${P.border}` }} data-testid="cv-nav">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex items-center gap-3 lg:gap-6">
        {/* Mobile hamburger — shifted right to clear the absolute "Back to BSGOTECH" button on top-left, and shrunk brand so it doesn't collide with the fixed LanguageSwitcher on top-right */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          data-testid="cv-nav-mobile-toggle"
          className="lg:hidden ml-[136px] inline-flex items-center justify-center w-9 h-9 rounded-md shrink-0"
          style={{ background: P.glass, border: `1px solid ${P.border}`, color: P.ink }}
        >
          {mobileOpen ? <X size={16} /> : <Menu size={16} />}
        </button>

        {/* Brand — hidden on mobile to avoid overlap with fixed LanguageSwitcher */}
        <div className="hidden lg:flex items-center gap-2 shrink-0 text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.gold }}>
          <MapPin size={12} /> BSGOTECH × CABO VERDE
        </div>

        {/* Desktop nav — hidden on small viewports */}
        <nav className="hidden lg:flex items-center gap-1 text-[12px] font-medium flex-1" data-testid="cv-nav-desktop">
          {NAV.map((item) => {
            const isActive = activeNav === item.id;
            const isOpen = openDrop === item.id;
            const baseStyle = {
              color: isActive ? P.gold : P.silver,
              background: isActive ? `${P.gold}18` : "transparent",
              border: `1px solid ${isActive ? `${P.gold}66` : "transparent"}`,
            };
            if (item.children) {
              return (
                <div key={item.id}
                     className="relative"
                     onMouseEnter={() => handleEnter(item.id)}
                     onMouseLeave={handleLeave}>
                  <a href={`#${item.target || item.id}`}
                     onClick={go(item.target || item.id)}
                     data-testid={`cv-nav-${item.id}`}
                     aria-haspopup="menu"
                     aria-expanded={isOpen}
                     className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md whitespace-nowrap transition"
                     style={baseStyle}>
                    {item.label}
                    <ChevronDown size={13} className="transition-transform" style={{ transform: isOpen ? "rotate(180deg)" : "none", opacity: 0.75 }} />
                  </a>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.14 }}
                      className="absolute left-0 mt-2 min-w-[300px] rounded-xl overflow-hidden"
                      style={{ background: "rgba(10,15,31,0.98)", border: `1px solid ${P.border}`, boxShadow: `0 24px 60px -20px rgba(0,0,0,0.85), 0 0 0 1px ${P.cyan}18` }}
                      role="menu"
                      data-testid={`cv-nav-${item.id}-dropdown`}
                    >
                      {item.parentItem && (
                        <a href={`#${item.parentItem.id}`}
                           onClick={go(item.parentItem.id)}
                           data-testid={`cv-nav-${item.id}-parent`}
                           role="menuitem"
                           className="flex flex-col gap-0.5 px-4 py-3 transition"
                           style={{ background: `${P.gold}0e`, borderBottom: `1px solid ${P.border}` }}
                           onMouseEnter={(e) => { e.currentTarget.style.background = `${P.gold}18`; }}
                           onMouseLeave={(e) => { e.currentTarget.style.background = `${P.gold}0e`; }}>
                          <span className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.gold }}>Project</span>
                          <span className="text-[15px] font-semibold" style={{ color: "#fff" }}>{item.parentItem.label}</span>
                          <span className="text-[11px]" style={{ color: P.dim }}>{item.parentItem.subtitle}</span>
                        </a>
                      )}
                      {item.groupTitle && (
                        <div className="px-4 pt-3 pb-1 text-[9.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>
                          {item.groupTitle}
                        </div>
                      )}
                      <div className="py-2">
                        {item.children.map((c) => {
                          const rowKey = c.key || c.id;
                          return (
                            <a key={rowKey}
                               href={`#${c.id}`}
                               onClick={go(c.id)}
                               data-testid={`cv-nav-${item.id}-${rowKey}`}
                               role="menuitem"
                               className="flex flex-col gap-0.5 px-4 py-2.5 transition hover:brightness-110"
                               style={{ color: P.ink, background: "transparent" }}
                               onMouseEnter={(e) => { e.currentTarget.style.background = `${P.cyan}0f`; }}
                               onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                              <span className="text-[13px] font-semibold" style={{ color: "#fff" }}>{c.label}</span>
                              <span className="text-[11px]" style={{ color: P.dim }}>{c.subtitle}</span>
                            </a>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            }
            return (
              <a key={item.id}
                 href={`#${item.id}`}
                 onClick={go(item.id)}
                 data-testid={`cv-nav-${item.id}`}
                 className="px-3 py-1.5 rounded-md whitespace-nowrap transition"
                 style={baseStyle}>
                {item.label}
              </a>
            );
          })}
        </nav>

        {/* Desktop CTA — right */}
        <a href="#contact" onClick={go("contact")}
           data-testid="cv-nav-cta"
           className="hidden lg:inline-flex items-center gap-2 px-4 py-1.5 text-[11.5px] font-semibold rounded-md whitespace-nowrap ml-auto"
           style={{ background: P.gold, color: "#0d0a02" }}>
          Contact BSGOTECH <ArrowUpRight size={12} />
        </a>

        {/* Mobile hamburger — now duplicated at right: remove */}
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="lg:hidden border-t" style={{ borderColor: P.border, background: "rgba(5,8,19,0.98)" }} data-testid="cv-nav-mobile-panel">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {NAV.map((item) => {
              const isGroupOpen = mobileGroup === item.id;
              if (item.children) {
                return (
                  <div key={item.id} className="rounded-md" style={{ border: `1px solid ${isGroupOpen ? `${P.gold}44` : "transparent"}` }}>
                    <button
                      type="button"
                      onClick={() => setMobileGroup(isGroupOpen ? null : item.id)}
                      data-testid={`cv-nav-mobile-${item.id}`}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-[13px] font-medium"
                      style={{ color: activeNav === item.id ? P.gold : P.ink }}
                    >
                      <span>{item.label}</span>
                      <ChevronDown size={14} style={{ transform: isGroupOpen ? "rotate(180deg)" : "none", transition: "transform 150ms", opacity: 0.7 }} />
                    </button>
                    {isGroupOpen && (
                      <div className="pb-2 pl-3 border-l ml-3" style={{ borderColor: `${P.gold}44` }}>
                        {item.parentItem && (
                          <a href={`#${item.parentItem.id}`}
                             onClick={go(item.parentItem.id)}
                             data-testid={`cv-nav-mobile-${item.id}-parent`}
                             className="flex flex-col gap-0.5 px-3 py-2 rounded-md"
                             style={{ background: `${P.gold}0e` }}>
                            <span className="text-[9.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.gold }}>Project</span>
                            <span className="text-[14px] font-semibold" style={{ color: "#fff" }}>{item.parentItem.label}</span>
                            <span className="text-[11px]" style={{ color: P.dim }}>{item.parentItem.subtitle}</span>
                          </a>
                        )}
                        {item.groupTitle && (
                          <div className="px-3 pt-2 pb-1 text-[9.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>
                            {item.groupTitle}
                          </div>
                        )}
                        {item.children.map((c) => {
                          const rowKey = c.key || c.id;
                          return (
                            <a key={rowKey}
                               href={`#${c.id}`}
                               onClick={go(c.id)}
                               data-testid={`cv-nav-mobile-${item.id}-${rowKey}`}
                               className="flex flex-col gap-0.5 px-3 py-2 rounded-md">
                              <span className="text-[13px] font-semibold" style={{ color: "#fff" }}>{c.label}</span>
                              <span className="text-[11px]" style={{ color: P.dim }}>{c.subtitle}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <a key={item.id}
                   href={`#${item.id}`}
                   onClick={go(item.id)}
                   data-testid={`cv-nav-mobile-${item.id}`}
                   className="px-3 py-2.5 rounded-md text-[13px] font-medium"
                   style={{ color: activeNav === item.id ? P.gold : P.ink, background: activeNav === item.id ? `${P.gold}14` : "transparent" }}>
                  {item.label}
                </a>
              );
            })}
            <a href="#contact" onClick={go("contact")}
               data-testid="cv-nav-mobile-cta"
               className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-[12.5px] font-semibold rounded-md"
               style={{ background: P.gold, color: "#0d0a02" }}>
              Contact BSGOTECH <ArrowUpRight size={13} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============ Documents sub-section ============ */
function DocumentsSection() {
  return (
    <section id="documents" className="py-24 px-6 lg:px-12 border-t" style={{ borderColor: P.border, background: P.bg2 }}>
      <div className="max-w-7xl mx-auto">
        <SectionHeader eyebrow="Documents & Resources"
          title="Institutional Document Library"
          subtitle="Access levels: PUBLIC (open information) · CONTROLLED (selected stakeholders) · CONFIDENTIAL (upon authorization)." />
        <div className="mt-10">
          <PremiumVisual src="/cabo-verde/visuals/14-document-library.webp"
            alt="Premium secure document environment — four floating document covers with color-coded access classifications (PUBLIC, CONTROLLED, CONFIDENTIAL)."
            tone="gold" testid="cv-visual-docs" />
        </div>
        <div className="mt-12 grid md:grid-cols-2 gap-4">
          {documents.map((d) => (
            <div key={d.id} className="rounded-xl p-6 flex flex-col"
                 style={{ background: P.surface, border: `1px solid ${P.border}`, borderTop: `2px solid ${P.gold}55` }}
                 data-testid={`cv-doc-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="w-10 h-10 rounded-md flex items-center justify-center"
                     style={{ background: `${P.gold}14`, border: `1px solid ${P.gold}55` }}>
                  <FileText size={18} style={{ color: P.gold }} />
                </div>
                <span className="text-[9.5px] uppercase tracking-[0.28em] font-semibold px-2.5 py-1 rounded-full"
                      style={{ background: `${P.gold}0d`, border: `1px solid ${P.gold}44`, color: P.gold }}>
                  {d.badge}
                </span>
              </div>
              <div className="mt-4 text-[16px] font-semibold tracking-tight" style={{ color: "#fff" }}>{d.title}</div>
              <div className="text-[13px]" style={{ color: P.silver }}>{d.subtitle}</div>
              <p className="mt-3 text-[12.5px] leading-relaxed" style={{ color: P.dim }}>{d.desc}</p>
              {d.prepared && (
                <div className="mt-3 text-[11.5px] italic" style={{ color: P.dim }}>{d.prepared}</div>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                {d.request ? (
                  <a href="#request-access" onClick={scrollTo("request-access")}
                     className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold rounded-md"
                     style={{ background: P.gold, color: "#0d0a02" }}
                     data-testid={`cv-doc-cta-${d.id}`}>
                    {d.cta} <ChevronRight size={12} />
                  </a>
                ) : (
                  <a href={d.href} target="_blank" rel="noopener noreferrer"
                     onClick={() => track(d.event)}
                     className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold rounded-md"
                     style={{ background: P.gold, color: "#0d0a02" }}
                     data-testid={`cv-doc-cta-${d.id}`}>
                    {d.cta} <ArrowUpRight size={12} />
                  </a>
                )}
                {d.secondary && (
                  <a href={d.secondary.href} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-semibold rounded-md"
                     style={{ background: P.glass, color: P.ink, border: `1px solid ${P.border}` }}>
                    {d.secondary.label} <ArrowUpRight size={12} />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.border}` }}>
          <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.silver }}>
            Additional Documentation Available on Request
          </div>
          <ul className="mt-4 grid md:grid-cols-2 gap-2 text-[12.5px]" style={{ color: P.dim }}>
            {[
              "Nu Bai Technical Architecture",
              "Nu Bai Way (payment pillar) Regulatory Approach",
              "Pilot Implementation Framework",
              "Financial Model",
              "Unit Economics",
              "Corporate Documentation",
              "Partner / Supplier Documentation",
            ].map((x) => (
              <li key={x} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ background: P.gold }} />{x}</li>
            ))}
          </ul>
          <a href="#request-access" onClick={scrollTo("request-access")}
             className="mt-5 inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md"
             style={{ background: P.gold, color: "#0d0a02" }}>
            Request Access <ChevronRight size={13} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============ Request Access form ============ */
function RequestAccessForm() {
  const [state, setState] = React.useState({ name: "", company: "", role: "", email: "", doc: "", reason: "" });
  const [sent, setSent] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    try {
      const res = await fetch(`${API}/api/strategic-contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: state.name.trim(),
          company: state.company.trim(),
          sector: state.role.trim() || "Cabo Verde Request",
          country: "Cabo Verde Request",
          email: state.email.trim(),
          message: `[Cabo Verde · Document Access Request]\nDocument: ${state.doc}\nReason: ${state.reason}`,
        }),
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      setSent(true);
      track("cabo_verde_request_access_submitted", { doc: state.doc });
    } catch (e) {
      setErr("Não foi possível enviar. Tente novamente ou envie email para admin@bsgotech.com.");
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div id="request-access" className="rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.gold}55` }}>
        <ShieldCheck size={20} style={{ color: P.gold }} />
        <div className="mt-3 text-[16px] font-semibold" style={{ color: "#fff" }}>Request received.</div>
        <p className="mt-2 text-[13px]" style={{ color: P.dim }}>
          Thank you. A member of the BSGOTECH team will review the request and follow up via email.
        </p>
      </div>
    );
  }

  return (
    <form id="request-access" onSubmit={submit} className="rounded-xl p-6" style={{ background: P.surface, border: `1px solid ${P.border}` }} data-testid="cv-request-form">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.gold }}>Request Access</div>
      <div className="mt-4 grid gap-3">
        {[
          ["name",    "Full Name",         "text"],
          ["company", "Organization",      "text"],
          ["role",    "Position",          "text"],
          ["email",   "Business Email",    "email"],
          ["doc",     "Document Requested","text"],
        ].map(([k, label, type]) => (
          <label key={k} className="block">
            <span className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.silver }}>{label}</span>
            <input required type={type} value={state[k]} onChange={(e) => setState({ ...state, [k]: e.target.value })}
                   className="mt-1 w-full rounded-md px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-[color:var(--fx)]"
                   style={{ background: P.bg2, border: `1px solid ${P.border}`, color: P.ink, "--fx": P.gold }}
                   data-testid={`cv-form-${k}`} />
          </label>
        ))}
        <label className="block">
          <span className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: P.silver }}>Reason for Request</span>
          <textarea required rows={3} value={state.reason} onChange={(e) => setState({ ...state, reason: e.target.value })}
                    className="mt-1 w-full rounded-md px-3 py-2 text-[13px] outline-none"
                    style={{ background: P.bg2, border: `1px solid ${P.border}`, color: P.ink }}
                    data-testid="cv-form-reason" />
        </label>
      </div>
      {err && <div className="mt-3 text-[12px]" style={{ color: "#ff9999" }}>{err}</div>}
      <button type="submit" disabled={loading} data-testid="cv-form-submit"
              className="mt-5 inline-flex items-center gap-2 px-5 py-3 text-[12.5px] font-semibold rounded-md disabled:opacity-60"
              style={{ background: P.gold, color: "#0d0a02" }}>
        {loading ? "Sending…" : "Send Request"} <ArrowUpRight size={13} />
      </button>
    </form>
  );
}
